import { Engine } from '@babylonjs/core/Engines/engine';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Scene } from '@babylonjs/core/scene';

import type { RoomDto, RoomTemplateDto } from '../generated/contracts';
import { BEAM_NAME_FACE_UV, buildBeamNameTexture } from '../room/RoomPlaque';
import { accentColorAt } from './AccentColorAnimation';
import { breathingIntensity, speakingIntensity } from './AssistantLightAnimation';
import { CELL_SIZE } from './GridSnap';
import {
  buildWallTexture,
  DEFAULT_WALL_TEXTURE_KEY,
  PANELS_WITH_GRAIN_TILE_HEIGHT_METRES,
  PANELS_WITH_GRAIN_TILE_WIDTH_METRES,
} from './WallTextures';
import type { WhiteboardLayout } from './WhiteboardLayout';

/**
 * The room-wide assistant state a light hung high in the room carries (plan/ai-in-room.md):
 * coloured and lit while a turn is open — including the "thinking" gap after it closes,
 * before an answer arrives — a different colour while the answer is actually playing, off
 * otherwise. A local type rather than one imported from `assistant/AssistantController`: that
 * module is a feature built on top of this one, not the other way round, and `main.ts` — the
 * composition root — is the one place that needs to know the two are the same four strings.
 *
 * `listening` (your own open turn) and `listening-remote` (someone else's) are visually
 * distinct — a two-person headset session reported the room showing one identical glow for
 * both, which read as "the channel is wide open for everyone" even though only one connection
 * actually held it. See `AssistantController`'s own `AssistantGlow` doc for the full story of
 * which call site sets which.
 *
 * Used to be carried by tinting a ceiling mesh's own material instead of lighting the room —
 * see `applyAssistantGlow`'s own doc comment for why that shipped, read as pure black, and was
 * replaced rather than debugged into working. The ceiling mesh itself is gone now too (a
 * second headset session: "way up high, with a big visible gap between the walls and the
 * ceiling... didn't buy us anything. Black is fine.") — see the note above the light's own
 * construction in `buildRoom`. The light was already independent of the ceiling by the time
 * that happened, which is why removing the mesh needed no change here.
 */
export type AssistantGlow = 'none' | 'listening' | 'listening-remote' | 'speaking';

/**
 * How far above the walls the assistant's light hangs — roughly where the ceiling used to sit
 * before it was removed entirely (see the note above the light's own construction in
 * `buildRoom`). `wallHeight` itself is untouched: the walls, the structural frame
 * (`FRAME_MEMBER_THICKNESS_METRES`) and the whiteboard layout all still key off it exactly as
 * before, so this cannot change any of them. The room has nothing above the
 * walls at all now, so this only controls the light's own position, not a surface it hangs
 * under.
 */
const ASSISTANT_LIGHT_HEIGHT_ABOVE_WALLS_METRES = 3.9;

/**
 * `PointLight.range` for the assistant's light. Not a soft-edged decoration — it is the entire
 * mechanism by which this light's brightness varies with distance at all. Confirmed by reading
 * the shader `StandardMaterial` compiles unless `falloffType` is changed
 * (node_modules/@babylonjs/core/Shaders/ShadersInclude/pbrDirectLightingFalloffFunctions.js,
 * `computeDistanceLightFalloff_Standard`): `max(0, 1 - distance / range)`, a linear falloff to
 * zero at `range` metres, not an inverse-square law. `PointLight.range` defaults to
 * `Number.MAX_VALUE` (node_modules/@babylonjs/core/Lights/light.js), so a light that never sets
 * it — this one, originally — evaluates that formula to effectively 1.0 at any distance a room
 * could ever put a viewer: there is no falloff at all, and the light lands at full intensity on
 * every surface in the room regardless of how far away it is. That is the actual mechanism
 * behind the headset report "the whole room lights up", not merely "the number was too high" —
 * the two would have looked identical from here, because nothing made distance matter until
 * this was set. Chosen so the light reaches the room's centre and walls at a real but
 * meaningfully reduced strength while fading out well before the far corners of the 8x8m
 * default room (`DbSeeder.BasicRoomFloorplan`, `src/backend/Tunet.Api/Data/DbSeeder.cs`) — a
 * first guess geometrically reasoned from the light's own height below, not yet confirmed on a
 * headset.
 */
const ASSISTANT_LIGHT_RANGE_METRES = 9;

/**
 * Base and swing for the 'listening' breath (see `tickAssistantLight`). Kept low — together
 * with `ASSISTANT_LIGHT_RANGE_METRES` above — so the light reads, per the headset report, as
 * "a coloured presence" rather than as room lighting. First guess, not yet confirmed on a
 * headset, the same honest status most of this project's untested visual constants carry.
 */
const LISTENING_BASE_INTENSITY = 0.35;
const LISTENING_BREATH_SWING = 0.35;

/** A slow breath, not a pulse: several seconds a cycle, because this runs for as long as the
 *  channel stays open and anything faster becomes irritating well before a long turn ends —
 *  the owner's own words: "subtle... anything fast or bright becomes irritating within
 *  seconds." */
const LISTENING_BREATH_PERIOD_MS = 4400;

/**
 * Base and gain for the 'speaking' state (see `tickAssistantLight` and
 * `AssistantController.onAmplitudeChanged`). Base keeps the light visibly lit through a quiet
 * moment in the clip rather than blacking out between words; gain is how much a full-volume
 * instant adds on top. Same "first guess" status as the listening constants above.
 */
const SPEAKING_BASE_INTENSITY = 0.25;
const SPEAKING_AMPLITUDE_GAIN = 0.85;

/**
 * Shared colours for the assistant's active states, read by both the room light above and
 * the sky stars below (`buildAssistantStars`/`applyAssistantGlow`) so the two channels say the
 * same thing by construction rather than by two call sites happening to be kept in sync by
 * hand. Values match what `applyAssistantGlow` used to set directly on `assistantLight.diffuse`
 * before this constant existed.
 */
const LISTENING_COLOR = new Color3(0.35, 0.5, 1);
const SPEAKING_COLOR = new Color3(0.3, 0.9, 0.45);

/**
 * 'listening-remote''s own colour — deliberately not blue: `LISTENING_COLOR` now means
 * specifically "this connection's own turn is open" (see `AssistantGlow`'s own doc comment), so
 * someone else's open turn needs a colour nobody could mistake for that, at a glance, from
 * across the room. Amber sits clearly apart from both the listening blue and the speaking green
 * in hue, the same "distinct, not just a different shade" bar `LISTENING_COLOR`/`SPEAKING_COLOR`
 * already had to clear against each other.
 */
const LISTENING_REMOTE_COLOR = new Color3(0.95, 0.6, 0.15);

/**
 * The sky stars — a second, independent visual channel for the assistant's state, alongside the
 * room light above (plan/ai-in-room.md). The room has no windows and a starfield skybox
 * (plan/skybox.md), so straight up is the only sky anyone in it can see; a scatter of extra
 * stars hung up there that pulse with the same state give a second place to notice it, without
 * touching the light's own mechanism at all. Built once, in the constructor, for exactly the
 * reason the skybox and ambient/sun lights are: a lifetime object, not a per-room one, and
 * `buildRoom()` is re-entrant per room rebuild (a reconnect's resync, most plausibly).
 * Deliberately not parented to `roomOrigin` (room-local, rebuilt per room) and deliberately not
 * pushed onto `this.disposables` (cleared on every `buildRoom()` — see `clearRoom`) — the same
 * two "lifetime object" traps the skybox's own comment above documents, worth restating here
 * rather than only there, since a future reader of the stars is unlikely to also read the
 * skybox's comment first.
 *
 * Originally 5-9 hand-authored offsets. A headset report — "clustered together really tightly
 * so you have to look straight up to see them" — found the cluster subtended only a couple of
 * degrees against `STAR_CLUSTER_HEIGHT_METRES`, and the owner asked for both a much wider spread
 * and a lot more stars, so the sky reads as a field rather than a knot directly overhead. A
 * bigger count changes the cost model: at 7 stars, 7 separate `CreateSphere` meshes sharing one
 * material was fine — 7 draw calls is nothing. At the count needed to read as "a field" (below),
 * a separate mesh per star would mean a separate draw call per star, which is the kind of cost a
 * mobile GPU (the Quest standalone browser this ships to) actually notices. `buildAssistantStars`
 * therefore builds each star as a temporary sphere, positions it, and merges the whole cluster
 * into a single `Mesh` via `Mesh.MergeMeshes` (CLAUDE.md: disposes its sources, so nothing about
 * a star is read from the temporaries after the merge call) — one draw call regardless of count,
 * and triangle count is cheap on any GPU this targets. `assistantStarsMesh` is what survives;
 * there is no longer a per-star `Mesh` to hold onto individually.
 *
 * Positions are generated by `buildStarOffsets`, a pure function of the star's index — not
 * `Math.random()` — so two runs of this file always produce the exact same sky, the same
 * requirement the original hand-authored array existed to satisfy. See that function's own
 * comment for the construction; procedurally generated is not the same mistake as "obviously
 * generated" (the single `i / count` evenly-spaced ring the original comment warned against) as
 * long as the *result* still reads as an irregular scatter, which the golden-angle spiral plus
 * per-star jitter below is specifically chosen to do.
 */
const STAR_COUNT = 220;

/** Height of the star cluster's own centre above the world origin, in metres. Comfortably above
 *  the room's own walls and `ASSISTANT_LIGHT_HEIGHT_ABOVE_WALLS_METRES`'s light, and comfortably
 *  inside `SKYBOX_SIZE_METRES / 2` (1000), so the cluster reads as part of the sky rather than
 *  as something hanging in the room. */
const STAR_CLUSTER_HEIGHT_METRES = 140;

/** Each star's diameter, in metres — small enough, at this height and against the room's own
 *  scale, to read as a point rather than a visible sphere, the same visual register as the
 *  skybox's own painted stars rather than dwarfing them. Unaffected by the move to a single
 *  merged mesh — each star is still its own little sphere's worth of geometry, just baked into
 *  one mesh's vertex data instead of kept as 220 separate `Mesh` instances. */
const STAR_DIAMETER_METRES = 1.1;

/** How far, at most, a star's horizontal (X/Z) offset from the cluster's own vertical axis can
 *  reach.
 *
 *  Raised from 260 to 700 for the second "hard to read" headset report (2026-08-14,
 *  `plan/open-items.md`, owner: "more spreading out is the key, especially now that the walls
 *  are semi-transparent"). "More stars" was already tried for the first report (9003bc3,
 *  2026-08-07: `STAR_COUNT` 7 -> 220) and is explicitly not the lever a second time; this constant
 *  is. At the *base* height (`STAR_CLUSTER_HEIGHT_METRES`, 140, no jitter),
 *  `atan(700 / 140)` is a little over 78.7° off straight up, up from the previous ~61.7° —
 *  noticeably closer to the horizon than to the zenith, comfortably within an ordinary upward
 *  glance rather than only straight overhead. Combined with `STAR_HEIGHT_JITTER_METRES` (raised
 *  alongside this constant, 55 -> 90), the actual range across the cluster runs from
 *  `atan(700 / (140 + 90))` ≈ 71.8° (a star jittered *up*, at the largest radius) to
 *  `atan(700 / (140 - 90))` ≈ 85.8° (a star jittered *down*, at the largest radius) — see that
 *  constant's own doc comment for why the low end of that range matters now that the room's walls
 *  are glass. Still comfortably inside `SKYBOX_SIZE_METRES / 2` (1000). */
const STAR_SPREAD_RADIUS_METRES = 700;

/** How far a star's height can wander above or below the cluster's own centre — kept well under
 *  `STAR_CLUSTER_HEIGHT_METRES` (140) so every star still reads as "up in the sky"
 *  (`SceneRoot.test.ts`'s own "well above the room" check asserts `y > 20`, satisfied here with
 *  room to spare: the lowest a star's own centre can sit is 140 - 90 = 50), while breaking up
 *  what would otherwise be a perfectly flat disc of stars.
 *
 *  Raised from 55 to 90 alongside `STAR_SPREAD_RADIUS_METRES` for the second "hard to read"
 *  report (2026-08-14, `plan/open-items.md`) — the room's walls and floor are semi-transparent
 *  glass now (`WALL_GLASS_ALPHA`/`FLOOR_GLASS_ALPHA`), so a viewer standing in the room sees the
 *  sky through the *walls* as well as through the open top, and the walls only let through the
 *  grazing, near-horizon part of the sky (a sightline steep enough to clear the wall's own top
 *  passes over it and shows the same open-top view the room always had). A star sitting exactly
 *  at `STAR_CLUSTER_HEIGHT_METRES`'s own height only ever grazes the horizon at the very edge of
 *  `STAR_SPREAD_RADIUS_METRES`'s own reach; pulling a star's *height* down as well as pushing its
 *  *radius* out is what lands some of the scatter inside the glass-wall band specifically, rather
 *  than only widening the already-visible open-top cone. See `STAR_SPREAD_RADIUS_METRES`'s own
 *  doc comment for the combined angular maths. */
const STAR_HEIGHT_JITTER_METRES = 90;

/** The golden angle, in radians — successive points placed this far apart in azimuth never
 *  repeat a radial spoke no matter how many are added, which is the standard trick (a
 *  "sunflower"/Vogel spiral) for filling a disc with points that look scattered rather than
 *  arranged on rings, without any actual randomness. */
const GOLDEN_ANGLE_RADIANS = Math.PI * (3 - Math.sqrt(5));

/**
 * A deterministic, seeded pseudo-random value in `[-1, 1)` for star index `i` — a standard
 * "hash via an irrational multiplier and a fractional sine" construction, used purely to break
 * up the spiral's own regularity (a small angular nudge, a height offset) without introducing
 * `Math.random()` or any other runtime non-determinism. Different `seed` values decorrelate the
 * angular jitter from the height jitter, so the two do not covary star-to-star.
 */
function pseudoNoise(i: number, seed: number): number {
  const raw = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453;
  return 2 * (raw - Math.floor(raw)) - 1;
}

/**
 * Star offsets from the cluster's own centre `(0, STAR_CLUSTER_HEIGHT_METRES, 0)`, one per
 * index in `[0, count)` — a Vogel/sunflower spiral (`GOLDEN_ANGLE_RADIANS`, `Math.sqrt` radial
 * density so the disc fills evenly rather than clumping toward the centre) with a small
 * deterministic angular and vertical jitter (`pseudoNoise`) so the result reads as a scatter
 * rather than a visibly regular spiral. Pure function of `count` alone — no hidden state, so
 * `buildAssistantStars` can call this directly rather than reading a fixed array.
 */
function buildStarOffsets(count: number): [number, number, number][] {
  const offsets: [number, number, number][] = [];

  for (let i = 0; i < count; i++) {
    const t = (i + 0.5) / count;
    const radius = STAR_SPREAD_RADIUS_METRES * Math.sqrt(t);
    const angle = i * GOLDEN_ANGLE_RADIANS + pseudoNoise(i, 3) * 0.35;

    const x = radius * Math.cos(angle);
    const z = radius * Math.sin(angle);
    const y = STAR_HEIGHT_JITTER_METRES * pseudoNoise(i, 7);

    offsets.push([x, y, z]);
  }

  return offsets;
}

/**
 * The skybox box's edge length, in metres. Nothing in this project ever sets a camera's
 * `maxZ` (confirmed by search — `XrManager.ts`, `main.ts`, and this file itself all leave it
 * untouched), so every camera here — the XR camera and the desktop fallback alike — runs on
 * Babylon's own default far clip plane, 10000 (`Camera.maxZ`,
 * node_modules/@babylonjs/core/Cameras/camera.js). 2000 sits comfortably inside that with a
 * wide margin either side: far enough out that `infiniteDistance` (below) never has to fight
 * visible parallax against the room's own 8x8m scale, and far enough inside 10000 that no
 * future, more conservative `maxZ` this project might set later would need to know about this
 * constant to avoid clipping it.
 */
const SKYBOX_SIZE_METRES = 2000;

/**
 * Hemispheric ambient's own intensity — pulled down from the 0.75 this room shipped with
 * (owner: "it looks really 'simple tech demo' today"). At 0.75, ambient alone lit every surface
 * from every direction almost as strongly as the single directional light on top of it, which is
 * exactly what reads as flat: a lit and a shaded side of the same shape were barely
 * distinguishable. Lower is not "dimmer room" — the two directional lights below make up the
 * difference on the lit side; only the shaded side actually gets darker, which is the point.
 */
const AMBIENT_INTENSITY = 0.32;

/**
 * The key light: the room's one dominant directional light, angled rather than overhead so a
 * shape's lit and shaded faces read differently. Warmer than neutral white — a hemispheric
 * ambient plus a single grey directional (the old 0.55-intensity `sun`) had no colour
 * temperature contrast at all, which is part of what made two evenly-lit lights still read as
 * flat. Direction is the old `sun`'s own, kept unchanged: a diagonal already reasoned about
 * skirting/picture-rail visibility and never reported as wrong on a headset.
 */
const KEY_LIGHT_INTENSITY = 0.7;
const KEY_LIGHT_COLOR = new Color3(1, 0.93, 0.82);
const KEY_LIGHT_DIRECTION = new Vector3(-0.4, -1, 0.5);

/**
 * The fill light: dimmer and cooler than the key, from a different angle, so a shape's shaded
 * side reads as dim and blue-tinted rather than falling all the way to black — enough to keep
 * every surface's "near" and "far" side visually distinct without a third strong light source or
 * any shadow map. This is the light that actually does the "give it a key and a fill" ask: the
 * key alone would leave the far side of every box pure ambient-grey, no less flat than before.
 */
const FILL_LIGHT_INTENSITY = 0.22;
const FILL_LIGHT_COLOR = new Color3(0.55, 0.68, 0.85);
const FILL_LIGHT_DIRECTION = new Vector3(0.55, -0.25, -0.6);

/**
 * Length of each half of the room accent's own transition — `from` eases to white over this
 * long, then white eases to the new colour over an equally long second half (the owner's own
 * shape: "changing", not "changed"). Applies only to a genuine colour change: `buildRoom`
 * skips the whole thing, including the very first room a client ever builds, when the target
 * already matches what is currently displayed (see `buildRoom`'s own `startAccent`/
 * `targetAccent` comparison) — there is nothing to ease into on first paint, only on an actual
 * `set_room_accent_color`/debug-panel trim change.
 */
const ACCENT_TRANSITION_PHASE_MS = 500;

/**
 * Alpha (opacity) for the room's walls and floor — an experiment the owner asked for directly:
 * "I want to test semi-transparent walls and floor. Just enough that it still feels solid, and
 * the trim pieces stay opaque, but the skybox is semi-visible through the rest of the walls and
 * floor as if it's tinted glass," on the theory that a more open-feeling room reads as less
 * claustrophobic. Applied to `wallMaterialNS`/`wallMaterialEW`/`floorMaterial` in
 * `buildWorkingRoomShell` only — the whiteboard's own frame used to be excepted here
 * (`BOARD_FRAME_COLOR`, black, not accent-tinted) until SPEC-190 removed the whiteboard outright
 * (plan/lobby-v1.md — no room, working or lobby, builds one any more); the picture rail this list
 * used to include is gone too, see `WALL_ACCENT_TINT_STRENGTH`'s own doc comment. `frameMaterial`
 * (the corner pillars and top/floor beams, added afterward — see `FRAME_COLOR`'s own doc comment)
 * never reads either constant and stays at `StandardMaterial`'s own default `alpha = 1`,
 * deliberately: opaque trim against glass panels is what keeps the room reading as a room
 * rather than as a floating grid. The wall materials' own *colour* (as opposed to this alpha)
 * now carries the room's accent tint — see `WALL_ACCENT_TINT_STRENGTH` — but that is a
 * completely separate property from the alpha this constant controls; retuning one never
 * implies retuning the other.
 *
 * Not settled by reasoning about what "feels solid" means in pixels — the owner's own acceptance
 * test is whether the room feels bigger, which is a look-at-it question a headset pass has to
 * answer, not this file. These two constants exist specifically so that pass can change the
 * number without touching anything else; expect at least one round of "try a different value"
 * once someone has actually looked.
 *
 * Deliberately left at Babylon's own default transparent sort — no `mesh.alphaIndex`, no
 * `renderingGroupId`, no `disableDepthWrite` — after reading the actual mechanism
 * (node_modules/@babylonjs/core/Rendering/renderingGroup.js, `_RenderSorted`/
 * `defaultTransparentSortCompare`): every alpha-blended submesh in a frame is sorted back-to-
 * front by its own bounding-sphere-*centre* distance to the camera, then rendered in that order
 * with depth-write left on (`Material.disableDepthWrite` defaults `false`,
 * node_modules/@babylonjs/core/Materials/material.js) — correct by construction for five large,
 * mostly non-overlapping panels (four walls plus the floor) viewed from inside the box, since a
 * typical camera position is unambiguously closer to whichever wall it's facing. The one place
 * this can still show a real artefact: `wall.size` for north/south spans the *full* room width
 * and east/west spans the full depth (see the `walls` array below), so each pair of adjacent
 * walls physically overlaps the other by a half-`thickness` (0.05 m, confirmed from the actual
 * position/size maths: e.g. north wall's z-extent is `[halfDepth - 0.05, halfDepth + 0.05]`,
 * entirely inside east wall's own `x`-extent `[halfWidth - 0.05, halfWidth + 0.05]` for the
 * `[halfWidth - 0.05, halfWidth]` slice) column at all four corners — genuine, pre-existing
 * geometry, invisible as long as both were opaque, but now two transparent surfaces occupying the
 * same few centimetres with only per-*mesh* (not per-pixel) sort granularity to arbitrate between
 * them. The floor/wall seam has the same shape, more thinly. Confined to a sliver at each corner
 * and the perimeter, not the walls' own readability.
 *
 * No longer merely flagged for a headset pass to look at: a corner pillar now stands at each of
 * these four corners, opaque and sized deliberately larger than the overlap it sits on top of
 * (`FRAME_MEMBER_THICKNESS_METRES`'s own doc comment) — the sliver this paragraph describes is
 * physically covered by an opaque member regardless of how the transparent sort resolves
 * underneath it, which makes the artefact structurally impossible rather than merely unlikely.
 */
const WALL_GLASS_ALPHA = 0.55;
const FLOOR_GLASS_ALPHA = 0.55;

/**
 * The neutral, pre-tint base the wall glass is blended from — `wallMaterialNS`/`wallMaterialEW`'s
 * own diffuse colour before `WALL_ACCENT_TINT_STRENGTH` mixes the room's accent into it. Used to
 * be the walls' entire colour, full stop, back when the room's accent lived only in the picture
 * rail and the doorway markers (both now gone — see `WALL_ACCENT_TINT_STRENGTH`'s own doc
 * comment).
 */
const WALL_BASE_COLOR = new Color3(0.9, 0.89, 0.87);

/**
 * How strongly the walls' own glass picks up the room's accent colour, `Color3.Lerp`'d against
 * `WALL_BASE_COLOR` (`tintedWallColor`, below) — 0 would leave the walls exactly as they were
 * before this change, 1 would paint them the accent colour outright with no base showing through
 * at all.
 *
 * This is the actual answer to a headset report that outlived the picture rail and the doorway
 * markers it arrived alongside (`plan/glass-room-tuning.md`): "we could just remove the accent
 * trim lines now and rather let the color be carried by a tint on the glass walls instead...
 * much less cramped than before while still being quite calm." So the trim strips
 * (`wall-*-rail`, one per wall, and the `doorway-*` markers) are gone outright — deleted, not
 * merely hidden — and this is what replaces them as the room's one identifying colour: every
 * wall's own diffuse colour, not a separate strip nailed to it.
 *
 * Chosen deliberately high rather than a light wash, for a second reason beyond "carry the
 * identity": the owner separately reported the walls reading "a bit washed out" and wanted them
 * "a tiny bit darker" — held at the time pending the white frame (#143), and still open once the
 * frame shipped (`plan/glass-room-tuning.md`, "Reported, deliberately held"). `WALL_BASE_COLOR`
 * is a near-white (luminance ~0.895); a typical room accent (the seeded rooms' own blues and
 * greens, `#4C8DFF`/`#3ED598`) sits at a meaningfully lower luminance and higher saturation. A
 * strong blend pulls the wall's overall value down and its saturation up as a direct consequence
 * of the same lerp that carries the identity colour — not a second, independent darkening
 * constant stacked on top (which would double-tune the same visual symptom against two unrelated
 * knobs). 0.6 was chosen as comfortably on the "strong" side of that trade — enough of the accent
 * showing to read as genuinely tinted glass rather than a faint wash, while `WALL_BASE_COLOR`
 * still contributes 40%, which is what keeps the procedural grain texture
 * (`wallMaterialNS.diffuseTexture`, multiplied against this diffuse colour) visible as brightness
 * variation rather than being swamped by a flat, fully-saturated slab of colour. Retune here,
 * alone, if a headset pass finds the walls still read washed out, or now too saturated — nothing
 * else needs to change to shift that balance.
 */
const WALL_ACCENT_TINT_STRENGTH = 0.6;

/**
 * The wall glass's own diffuse colour for a given accent — `WALL_BASE_COLOR` blended toward
 * `accent` by `WALL_ACCENT_TINT_STRENGTH`. Called once in `buildRoom` (against `startAccent` —
 * paint the current frame's colour immediately, let `accentAnim`/`tickAccentColor` ease the rest
 * of the way if one is running) and again every frame from `tickAccentColor` while a transition
 * is running (against the transition's own evolving `step.color`), so a live accent change
 * retints both wall materials exactly in step — one colour, read from one place, painted onto
 * the working room's one remaining accent-carrying surface (the whiteboard's own frame used to be
 * a second, black one — see `BOARD_FRAME_COLOR`'s own doc comment before it was deleted under
 * SPEC-190). Never called for the lobby shell, which does not carry the room's accent at all
 * (`buildLobbyShell`'s own doc comment).
 */
function tintedWallColor(accent: Color3): Color3 {
  return Color3.Lerp(WALL_BASE_COLOR, accent, WALL_ACCENT_TINT_STRENGTH);
}

/**
 * The floor's primary metre-grid line colour (`paintGridTexture`) — a fixed neutral grey rather
 * than the room's own accent colour, which is what this used to render in
 * (`accent.toHexString()`, before this constant existed). The owner's own words: "leave the grid
 * lines on the floor, but make it gray instead of black" — asked for alongside
 * `WALL_GLASS_ALPHA`/`FLOOR_GLASS_ALPHA` above, once the floor itself started reading as glass
 * rather than a solid colour. Accent already has a job — tinting the trim for spatial memory
 * between rooms — and tying the grid to it too meant the grid's own readability depended on
 * whatever colour a given room happened to have picked, which is an unnecessary coupling now
 * that the floor is meant to read as a consistent material rather than a solid accent-tinted
 * surface. `paintCellGrid`'s own separate `#c9d1e0` (the toggleable, finer debug overlay) is
 * intentionally a different, already-neutral colour and is untouched by this change.
 */
const FLOOR_GRID_LINE_COLOR = '#9aa0ac';

/**
 * The room's new structural frame — opaque white members along the box's own edges (a pillar
 * at each of the four corners, a beam along each wall's own top edge, and a beam along each
 * floor/wall seam) so the walls' and floor's own glass alpha (`WALL_GLASS_ALPHA`/
 * `FLOOR_GLASS_ALPHA` above) reads as panels held in a box rather than as floating tinted
 * sheets. The owner's own words, after seeing the glass walls: "We might want to add a white
 * pillar at each corner anyway, maybe even along the floor/wall seams and on top of the walls
 * so it feels like a box with glass panels."
 *
 * Deliberately its own colour, not the walls' own tinted diffuse (`WALL_ACCENT_TINT_STRENGTH`):
 * the owner said "white" while the walls themselves carry the room's accent tint and change per
 * room — the room's own identifying colour, the whole point of the memory-palace framing being
 * that rooms feel distinguishable. Tinting the structural frame the same way would erase the
 * per-room identity that surface exists to carry; keeping the frame in its own named constant
 * instead means either direction — the frame following the accent, or the walls going white — is
 * a one-line change once someone has actually looked at this on a headset.
 *
 * The whiteboard used to mount its own second frame, `BOARD_FRAME_COLOR` — a deliberately
 * separate black, not this constant's white, because the two frames served different surfaces
 * (this one runs the whole room; that one bordered only the board). SPEC-190 removed the
 * whiteboard outright (plan/lobby-v1.md), and its frame constant went with it — nothing here
 * needs to contrast against it any more, but the history is worth keeping: `FRAME_COLOR` itself
 * used to be accent-tinted too, until the picture rail was removed (`WALL_ACCENT_TINT_STRENGTH`'s
 * own doc comment).
 */
const FRAME_COLOR = new Color3(0.94, 0.94, 0.94);

/**
 * Cross-section of every new frame member, in metres — the corner pillars' own square
 * footprint, and the perpendicular-to-wall depth *and* vertical height of the top/floor beams
 * (one shared constant, so a pillar and a beam meeting at a joint read as the same structural
 * language rather than a mismatched assortment of trim widths).
 *
 * Sized deliberately against two things already in this file, not merely "looks about right":
 *
 * 1. The walls' own pre-existing corner overlap (`WALL_GLASS_ALPHA`'s own doc comment: north/
 *    south and east/west walls physically overlap by half of `thickness` (0.1m) at each of the
 *    four corners — full box spans, not butted edges — a plausible sliver-sized sort artefact
 *    now the walls are glass). A pillar exactly `thickness` (0.1m) square, centred on the same
 *    corner point the walls' own centrelines already meet at, would already cover that overlap
 *    flush with both walls' faces; this is comfortably larger again so the pillar stands proud
 *    of both wall faces by 0.04m on every side, covering the artefact with real margin rather
 *    than an exact, zero-tolerance fit — making it structurally impossible for any sliver of
 *    the walls' own seam to ever be exposed, not merely unlikely.
 * 2. *Formerly* also sized against the picture rail's own thinner protrusion (`thickness * 1.4` =
 *    0.14m, half 0.07m beyond the wall's own centreline), so a white frame member and an
 *    accent-coloured trim member landing on the same corner footprint would never tie at an
 *    exactly coincident face — a real z-fighting risk between two genuinely different colours,
 *    unlike the harmless same-colour wall-on-wall overlap reason 1 describes. The rail itself is
 *    gone now (`plan/glass-room-tuning.md` item 1 — the accent moved onto the wall glass instead,
 *    see `WALL_ACCENT_TINT_STRENGTH`), so that particular collision no longer exists. The value
 *    is left exactly as it was regardless: reason 1 alone already asks for comfortable margin,
 *    and `RoomPlaque.ts` now derives the plaque's own vertical fit against this exact figure too
 *    — the plaque mounts on the top beam's own face (`plan/glass-room-tuning.md`, "the plaque
 *    mounts on the white top beam"), spanning from `wallHeight - FRAME_MEMBER_THICKNESS_METRES`
 *    (the beam's underside) up to `wallHeight` (the beam's top, flush with the ceiling) — moving
 *    this number would move the plaque's band, and resize it, along with the beam.
 *
 * Exported so `RoomPlaque.ts` can share this exact figure rather than restating a second copy
 * that could drift from it — the plaque's own vertical band is derived directly against the top
 * beam this constant sizes (see that file's own module comment).
 */
export const FRAME_MEMBER_THICKNESS_METRES = 0.18;

/**
 * Whether the frame's top-of-wall beam is built at all. On by default; kept as its own flag
 * rather than folded into `FRAME_MEMBER_THICKNESS_METRES` because the owner's own request left
 * the top beam's existence, not merely its size, genuinely open ("maybe even ... on top of the
 * walls") — a one-line way to drop it if a headset pass finds it reads as lowering the ceiling
 * rather than framing the glass. The corner pillars and floor beam have no equivalent flag:
 * both were asked for outright, not hedged.
 */
const FRAME_TOP_BEAM_ENABLED = true;

/**
 * SPEC-198's own words: "a simple, well-lit circular space." Everything below this point, up to
 * `buildLobbyShell` itself, is that shell's own tuning — deliberately not reusing a single
 * constant from the working room's own glass-and-accent look above (`WALL_GLASS_ALPHA`,
 * `WALL_ACCENT_TINT_STRENGTH`, `FRAME_COLOR`...): the two are meant to read as different places at
 * a glance (SPEC-189), and sharing a constant between them would make a future retune of one
 * silently retune the other. First-guess values, plotted top-down and looked at
 * (`msedge --headless=new --screenshot`, this method's own doc comment) rather than measured on a
 * headset — the same honest status most of this project's untested visual constants carry.
 */

/** Tessellation for the lobby's circular floor and wall — high enough that a circle this size
 *  reads as round rather than faceted from a normal viewing distance, cheap enough that it costs
 *  nothing worth measuring on the Quest's own mobile GPU (this is two meshes, not 220 of them —
 *  contrast `STAR_COUNT`'s own reasoning for when tessellation-like counts start to matter). */
const LOBBY_TESSELLATION = 48;

/** The floor disc's own thickness, in metres — thin enough to read as a flat floor plane (the
 *  same "thin band, never seen edge-on from inside the room" role `FLOOR_GLASS_ALPHA`'s working-
 *  room floor plays at zero thickness), non-zero because `CreateCylinder`'s top cap is what gives
 *  this mesh a normal guaranteed to point straight up without any rotation to get wrong — see
 *  `buildLobbyShell`'s own doc comment for why a cylinder was chosen over a rotated disc. */
const LOBBY_FLOOR_THICKNESS_METRES = 0.05;

/**
 * The wall and floor colours, and the reason they are two constants rather than one.
 *
 * **The first version of this shell shipped to a headset as a featureless white void**, and the
 * owner's report was that the diagrams in it were impossible to see. The cause was arithmetic
 * rather than taste, and it is worth stating because every part of it looked individually
 * reasonable: one near-white colour (0.88) for both surfaces, a dedicated hemispheric light at
 * 0.85 *on top of* the scene-wide ambient/key/fill, and an emissive term at 0.35 of the diffuse
 * on top of that. `StandardMaterial` sums every light's contribution and clamps at 1.0, so the
 * two hemispherics alone (0.32 + 0.85) already drove a 0.88 diffuse past the clamp before either
 * directional light or the emissive was added. Every face of both meshes resolved to pure white,
 * which means no shading gradient survived anywhere: the curved wall could not read as curved,
 * the floor could not be told from the wall, and there was no horizon.
 *
 * What that cost is the lobby's actual job. A miniature is the diagram rendered small (SPEC-155,
 * SPEC-191) and several catalogue silhouettes are pale grey or white — against a pure-white
 * shell those are not merely low-contrast, they are gone, so the picker showed some diagrams'
 * shapes and silently swallowed others.
 *
 * So these are mid-tones now, and different from each other. "Well lit" is a description of the
 * *quality* of light in a room — even, open, no gloom in the corners — not an instruction to
 * maximise luminance, and a room rendered at the clamp has no light in it at all in the sense
 * that matters. The floor is the darker of the two so the floor/wall seam reads as a horizon,
 * which is most of what makes an enclosed space feel like somewhere you are standing.
 *
 * Still deliberately not shared with the working room's own constants, for the reason the block
 * comment above gives: the two must be free to be retuned apart.
 */
const LOBBY_WALL_COLOR = new Color3(0.74, 0.76, 0.82);
const LOBBY_FLOOR_COLOR = new Color3(0.3, 0.32, 0.38);

/**
 * The lobby hemispheric's own downward colour, as a fraction of the surface colour it lights.
 *
 * A `HemisphericLight` shades by blending its `diffuse` (for normals facing its direction) toward
 * its `groundColor` (for normals facing away), so `groundColor` is the *only* thing giving a
 * hemispheric light any gradient at all. The first version set it to the full surface colour,
 * which flattened that gradient to nothing — a second reason, independent of the clamp above,
 * that the wall could not read as curved. Kept well below 1 so downward-facing faces genuinely
 * fall off.
 */
const LOBBY_GROUND_FRACTION = 0.35;

/**
 * The lobby's own dedicated ambient light, on top of the scene-wide ambient/key/fill built once
 * in the constructor (`AMBIENT_INTENSITY` et al.) — those three are shared with the working room
 * and deliberately dimmed for it (`AMBIENT_INTENSITY`'s own doc comment: "it looks really 'simple
 * tech demo'"), so retuning them to make the lobby "well lit" would undo that tuning every time a
 * diagram is open instead. A second, lobby-only `HemisphericLight` built in `buildLobbyShell` and
 * torn down with the rest of that shell (`this.disposables`) is additive brightness that only
 * ever exists while the lobby itself is showing.
 *
 * Hemispheric rather than a `PointLight`: a point light's brightness falls off with distance
 * (CLAUDE.md, "A `PointLight` with no `range` does not fall off at all" — the trap this
 * deliberately avoids by construction rather than by remembering to set `range` correctly) and a
 * "simple, well-lit" room should not read as dim in its own corners relative to its centre. A
 * hemispheric light has no distance term to get wrong at all.
 */
const LOBBY_AMBIENT_INTENSITY = 0.32;

/**
 * Engine, scene, lighting, and the room shell built from its template.
 *
 * Everything is deliberately cheap: StandardMaterial, no shadows, no PBR. The target is a
 * Quest running standalone at 72 fps on a mobile GPU, and the content that matters —
 * shapes, labels, other people — has to fit in that budget, not the walls.
 */
export class SceneRoot {
  readonly engine: Engine;
  readonly scene: Scene;

  /** Room-local space. Node transforms from the server are relative to this. */
  readonly roomOrigin: TransformNode;

  /**
   * Valid teleport destinations. Populated when the room is built.
   *
   * Handed to Babylon's teleportation feature, which keeps this exact array instance
   * rather than copying it. That is why the array is only ever mutated in place: a rebuild
   * clears and refills it, and teleport targets follow for free. Reassigning it would
   * silently leave teleportation aiming at meshes that no longer exist.
   */
  readonly floorMeshes: Mesh[] = [];

  /**
   * The room's canonical entry point: just inside the south wall, looking across the room.
   *
   * Not the exact centre, which is where content collects — arriving inside a stack of
   * shapes is disorienting, and standing at the near edge shows the whole room at once.
   *
   * Used to be where recentring put you — `XrManager`'s off-hand recentre button read this
   * through its own `spawn` option. Both are gone: the same physical button collided with
   * the assistant's talk binding and was removed in favour of it (plan/ai-in-room.md), so
   * this currently has no reader. Left in place rather than deleted — it is a fact about the
   * room, cheap to keep, and exactly what a future "return to start" affordance would read.
   */
  readonly spawnPoint = new Vector3(0, 0, -2.5);

  /**
   * Facing at the spawn point, in radians of yaw. Zero looks along +Z, into the room.
   * Same currently-unread status as `spawnPoint` above — see its own doc comment.
   */
  readonly spawnYaw = 0;

  // spec: SPEC-190
  /**
   * The north wall's drawing surface, in room-local space — always `null` now. `buildRoom` used
   * to build a framed whiteboard on the working room's north wall (plan/whiteboard.md, "One wall,
   * framed") and set this field from it; SPEC-190 ("a whiteboard is not a fixture every room
   * arrives with," plan/lobby-v1.md) removed that construction outright, from both shells, so
   * nothing ever assigns this any more. Left in place, still typed `WhiteboardLayout | null`
   * rather than dropped or hardcoded to `null`, because every consumer — `DrawingManager`,
   * `ScanTool`, `ToolMode.eraserTool`/`.penColors` — already reads it as a possibly-absent
   * whiteboard and degrades correctly on `null` (checked: none of them crash); deleting the field
   * would be a second, larger change to all of them for no behavioural gain over simply never
   * populating it. See `ToolMode`'s own doc comment for how a `null` whiteboard also removes Draw
   * and Scan from the reachable mode cycle, rather than merely leaving them silently inert.
   */
  whiteboard: WhiteboardLayout | null = null;

  /**
   * The assistant's own light, hung high in the room — see
   * `ASSISTANT_LIGHT_HEIGHT_ABOVE_WALLS_METRES`'s own doc comment for exactly how high.
   * Tracked separately from every other disposable so `setAssistantGlow` has something live to
   * drive between rebuilds — the same shape a `ceilingMaterial` field used to serve back when a
   * material tint on a ceiling mesh, not a light, was the mechanism (see `applyAssistantGlow`'s
   * own doc comment). That mesh is gone now too, and never had a field of its own to begin
   * with — nothing outside `buildRoom` read it even while it existed. Reassigned on every
   * `buildRoom`; not exported — `setAssistantGlow` is the only way to touch this, and
   * `assistantGlow` below is the only way to read its current state back out.
   */
  private assistantLight: PointLight | null = null;

  /** Held across rebuilds so a resync mid-turn does not silently reset the glow — see
   *  `buildRoom`'s own call to `applyAssistantGlow` right after this is (re)created. */
  private currentGlow: AssistantGlow = 'none';

  /** When the current 'listening' stretch began, in `performance.now()` terms — the breathing
   *  wave's own time origin, so it starts calm and swells rather than starting already
   *  mid-swing. Reset on every fresh transition into 'listening'; read by `tickAssistantLight`.
   *  Null outside 'listening', where nothing reads it. */
  private listeningStartedAtMs: number | null = null;

  /** The 'speaking' state's own latest sample — see `setAssistantAmplitude` and
   *  `AssistantController.onAmplitudeChanged`. Merely recorded here; `tickAssistantLight` is
   *  what actually paints it onto the light, every rendered frame, the same "poll, don't push"
   *  split `RoomView` mirroring `RoomState` already uses elsewhere in this project. Reset to 0
   *  on every fresh transition into 'speaking', so a stale amplitude from a previous reply can
   *  never leak into the first instant of the next one. */
  private speakingAmplitude = 0;

  /**
   * The sky stars — see `STAR_COUNT`'s own doc comment for the design. A lifetime object built
   * once in the constructor (`buildAssistantStars`), never touched by `buildRoom`/`clearRoom`,
   * so this is set exactly once and never rebuilt or disposed across a room rebuild. A single
   * merged mesh, not one per star — see `STAR_COUNT`'s own doc comment for why the count made
   * that necessary. `null` only for the instant before `buildAssistantStars` runs in the
   * constructor; nothing outside this class can observe that instant.
   */
  private assistantStarsMesh: Mesh | null = null;

  /** One shared material for every star — they always show the identical colour and intensity
   *  at any given instant, so one `StandardMaterial` is both cheaper and simpler than one per
   *  star; `tickAssistantStars` only ever has one place to write the current frame's colour. */
  private assistantStarMaterial: StandardMaterial | null = null;

  /**
   * The accent colour actually being painted right now — which, mid-transition, is neither the
   * previous nor the next accent but wherever `tickAccentColor` last left it. Read as the
   * *start* point of the next transition (`buildRoom`'s own `startAccent`), so a second accent
   * change arriving mid-ease continues smoothly from the current frame rather than snapping back
   * to whatever the first change's own target was. `null` only before the very first
   * `buildRoom()` — after that this is always set, even outside a transition, so "no transition
   * running" is `accentAnim === null`, not "this is null".
   */
  private displayedAccentColor: Color3 | null = null;

  /**
   * The in-flight accent transition, if any — set by `buildRoom` when the target differs from
   * `displayedAccentColor`, advanced every frame by `tickAccentColor`, and cleared the instant it
   * reaches `to` or the room is rebuilt again (`clearRoom`) out from under it. No `material`
   * field — the wall glass (`wallMaterialNS`/`wallMaterialEW` below) is the working room's one
   * remaining accent-carrying surface (the whiteboard's own frame carried a second, black one
   * until SPEC-190 removed the whiteboard outright), and `tickAccentColor` already reads those
   * two fields directly rather than needing this object to point back at a material to paint.
   * `null` while the lobby shell is showing — `buildLobbyShell` never assigns either field, since
   * the lobby does not carry the room's accent at all (see that method's own doc comment).
   */
  private accentAnim: { from: Color3; to: Color3; startedAtMs: number } | null = null;

  /**
   * The wall glass's own materials for the current room — see `WALL_ACCENT_TINT_STRENGTH`'s own
   * doc comment for why these are the room's one remaining accent-carrying surface. Tracked as
   * fields so `updateRoomAccent` can retint them live, every frame a transition is running,
   * whether that transition was started by `buildRoom` or by `updateRoomAccent` — no rebuild, no
   * shader recompile, which is the entire reason `updateRoomAccent` exists (see its own doc
   * comment). Two fields, not one, because north/south and east/west already need genuinely
   * different materials (different UV tiling — see `buildRoom`'s own wall-material comment); both
   * always exist or neither does, so any code that null-checks one before writing to it does the
   * same for the other. `null` only before the first `buildRoom()`, reset to `null` by `clearRoom`
   * (never disposed by that reset — both are pushed onto `disposables`, disposed from there).
   */
  private wallMaterialNS: StandardMaterial | null = null;
  private wallMaterialEW: StandardMaterial | null = null;

  /**
   * The floor grid's own `DynamicTexture` for the current room, kept live the same way the wall
   * materials above are — so `updateRoomAccent` can redraw its lines in the new colour without
   * disposing and recreating the texture (and therefore without the material ever needing to
   * recompile). Paired with `floorGridWidthMetres`/`floorGridDepthMetres` below, since redrawing
   * needs the room's own floor dimensions and nothing else already carries them between calls.
   */
  private floorGridTexture: DynamicTexture | null = null;
  private floorGridWidthMetres = 0;
  private floorGridDepthMetres = 0;

  /**
   * Whether the finer, `CELL_SIZE`-spaced grid (plan/grid.md) is drawn on top of the floor's
   * existing one-metre grid. Off by default — the owner has not yet decided whether snapping
   * on release should read as intentional (which argues for the grid being visible) or as a
   * bug (if it is not) — see `GridSnap.ts`'s own module comment and `DebugPanel.ts`'s
   * `toggle-grid` button, which is the only thing that flips this. Repainted, not rebuilt: see
   * `setCellGridVisible`.
   */
  private cellGridVisible = false;

  private readonly disposables: { dispose(): void }[] = [];

  /**
   * `engineAndScene` lets a test supply a headless engine instead of a real WebGL-backed
   * one. A canvas-driven `Engine` needs an actual GPU context, which a Node/jsdom test
   * environment cannot provide the way `NullEngine` can — every other Babylon-touching
   * test in this project sidesteps the problem by never constructing SceneRoot at all,
   * but the whiteboard's own geometry test (SceneRoot.test.ts) needs the real, unmodified
   * buildRoom() to run against a real floorMeshes array, not a reimplementation of it.
   * Production code never passes this.
   */
  constructor(readonly canvas: HTMLCanvasElement, engineAndScene?: { engine: Engine; scene: Scene }) {
    this.engine =
      engineAndScene?.engine ??
      new Engine(canvas, true, {
        // Quest Browser reports a devicePixelRatio above 1; letting Babylon pick its own
        // buffer scale keeps the render target sane rather than supersampling for free.
        preserveDrawingBuffer: false,
        stencil: false,
        antialias: true,
      });

    this.scene = engineAndScene?.scene ?? new Scene(this.engine);
    this.scene.clearColor = new Color4(0.08, 0.09, 0.11, 1);

    // Ambient fill plus a warm key and a cooler, dimmer fill — three lights, no shadow maps
    // (plan/lighting doc: no PBR, no DefaultRenderingPipeline; the target is 72fps stereo on a
    // Quest, and none of these are per-pixel-expensive). See AMBIENT_INTENSITY/KEY_LIGHT_*/
    // FILL_LIGHT_*'s own doc comments for what each number is doing and why.
    const ambient = new HemisphericLight('ambient', new Vector3(0, 1, 0), this.scene);
    ambient.intensity = AMBIENT_INTENSITY;
    ambient.groundColor = new Color3(0.3, 0.31, 0.35);

    const key = new DirectionalLight('key-light', KEY_LIGHT_DIRECTION, this.scene);
    key.intensity = KEY_LIGHT_INTENSITY;
    key.diffuse = KEY_LIGHT_COLOR;

    const fill = new DirectionalLight('fill-light', FILL_LIGHT_DIRECTION, this.scene);
    fill.intensity = FILL_LIGHT_INTENSITY;
    fill.diffuse = FILL_LIGHT_COLOR;

    // The skybox: a static backdrop, built once here rather than in buildRoom() for the same
    // reason ambient/sun above are — buildRoom() is re-entrant per room rebuild (a reconnect's
    // resync, most plausibly), and a skybox is a property of the client's own view, not of any
    // particular room's template, so it has no business being torn down and rebuilt alongside
    // walls and floor it has nothing to do with (plan/skybox.md). Deliberately NOT pushed onto
    // `this.disposables` for the same reason: that array is exactly what `clearRoom()` clears
    // on every `buildRoom()` call (see `clearRoom` below), so pushing a lifetime object onto it
    // would tear the skybox down on the room's very first rebuild — caught by this file's own
    // "survives a rebuild" test, which failed with the skybox missing entirely after a second
    // `buildRoom()` until this was fixed. `ambient`/`sun` above already establish the pattern
    // this follows: nothing explicitly disposes them either — `SceneRoot.dispose()`'s own
    // `this.scene.dispose()` call is what actually frees a scene-lifetime object.
    //
    // `new CubeTexture('/skybox/spacebox2/sky', this.scene)` with no explicit suffix list:
    // CubeTexture's own default is exactly ['_px.jpg', '_py.jpg', '_pz.jpg', '_nx.jpg',
    // '_ny.jpg', '_nz.jpg'] (node_modules/@babylonjs/core/Materials/Textures/cubeTexture.js),
    // and the six files under public/skybox/spacebox2/ are named to match it. The left/right
    // faces are deliberately crossed relative to the source pack's own naming — see
    // tools/skybox/README.md and plan/skybox.md for the seam-continuity measurement that
    // proved it (3.1 mean absolute difference swapped vs 59.5 as-named).
    const skyboxTexture = new CubeTexture('/skybox/spacebox2/sky', this.scene);
    skyboxTexture.coordinatesMode = Texture.SKYBOX_MODE;

    const skyboxMaterial = new StandardMaterial('skybox', this.scene);
    // Unlit and unshaded: this is a texture painted on the inside of a box, not a surface that
    // should react to the room's own lights. diffuseColor/specularColor both black plus
    // disableLighting keep it from picking up any tint from ambient/sun above or from a future
    // light added to the room.
    skyboxMaterial.disableLighting = true;
    skyboxMaterial.diffuseColor = Color3.Black();
    skyboxMaterial.specularColor = Color3.Black();
    skyboxMaterial.reflectionTexture = skyboxTexture;
    // Deliberately set to false, despite CLAUDE.md's own long entry on disabled back-face
    // culling shipping a real, hard-to-spot bug (the mirrored-label case, "DOUBLESIDE plus
    // disabled culling..."). That entry's mechanism does not apply here: it depended on two
    // *coincident* triangle sets (DOUBLESIDE) resolving a depth tie in the wrong order. This
    // box has one triangle set per face, no DOUBLESIDE, and no depth tie — the viewer stands
    // *inside* it, so the box's front faces (which point outward, away from the camera, by
    // construction) are never the ones that should be seen; only the back faces, facing
    // inward, are ever in view. Disabling culling here is what makes those back faces render
    // at all; leaving culling on (the material default) would make the entire box invisible
    // from the inside, in every direction, which the label bug's fix was never about.
    skyboxMaterial.backFaceCulling = false;

    const skybox = CreateBox('skybox', { size: SKYBOX_SIZE_METRES }, this.scene);
    skybox.material = skyboxMaterial;
    // Never translates with the player and is never clipped by the far plane regardless of
    // where the camera actually sits inside SKYBOX_SIZE_METRES — the whole point of a skybox.
    skybox.infiniteDistance = true;
    // Critical: both Babylon's teleportation ray and ConnectionDrawer's own aim ray pick
    // against the scene, and this box encloses the entire world. Pickable, it would swallow
    // every far-interaction in the product the instant nothing closer was in the way.
    skybox.isPickable = false;
    // Deliberately NOT parented to roomOrigin (that is room-local space, rebuilt per
    // buildRoom()) and deliberately NOT pushed into floorMeshes (that array is shared by
    // reference with the teleportation feature and must contain only real, walkable floor).

    this.roomOrigin = new TransformNode('roomOrigin', this.scene);

    // The sky stars — see STAR_COUNT's own doc comment. Built once, here, for the same
    // lifetime-object reason the skybox above is: not room state, so buildRoom()/clearRoom()
    // never touch it.
    this.buildAssistantStars();

    // Animates the assistant light every rendered frame — registered once, here, the same
    // lifetime ambient/sun above have, rather than in buildRoom(): the light itself is
    // recreated on every rebuild, but the observer driving it does not need to be, and
    // Scene.dispose() (confirmed by reading node_modules/@babylonjs/core/scene.js directly:
    // dispose() calls `this.onBeforeRenderObservable.clear()`) already tears this down along
    // with everything else this.dispose() disposes, so there is nothing to remove by hand.
    // Stars are ticked in the same observer, right after the light, so the two channels are
    // painted from the same frame's state rather than by two independently-scheduled callbacks.
    this.scene.onBeforeRenderObservable.add(() => {
      this.tickAssistantLight();
      this.tickAssistantStars();
      this.tickAccentColor();
    });

    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Builds the fixed cluster of sky stars once, at construction — see `STAR_COUNT`'s own doc
   * comment for why they exist, why this runs here rather than in `buildRoom()`, and why the
   * cluster is one merged mesh rather than `STAR_COUNT` separate ones. Builds `STAR_COUNT`
   * temporary spheres, positions each with `buildStarOffsets`, then merges them into a single
   * mesh — the temporaries are disposed as a side effect of `Mesh.MergeMeshes` itself
   * (CLAUDE.md: "disposes its sources"), so nothing here reads them again afterward.
   *
   * Starts fully invisible (`isVisible = false`) — 'none' must be fully invisible, not merely
   * dim, or every other state has to visually out-compete an idle glow that should not exist at
   * all.
   */
  private buildAssistantStars(): void {
    const material = new StandardMaterial('assistant-star', this.scene);
    material.disableLighting = true;
    material.diffuseColor = Color3.Black();
    material.specularColor = Color3.Black();
    material.emissiveColor = Color3.Black();
    this.assistantStarMaterial = material;

    const parts: Mesh[] = [];

    for (const [index, [dx, dy, dz]] of buildStarOffsets(STAR_COUNT).entries()) {
      const star = CreateSphere(
        `assistant-star-part-${index}`,
        { diameter: STAR_DIAMETER_METRES, segments: 8 },
        this.scene,
      );

      star.position.set(dx, STAR_CLUSTER_HEIGHT_METRES + dy, dz);
      star.material = material;
      // Not parented to anything before the merge — MergeMeshes bakes each part's world
      // matrix into the merged mesh's vertex data (confirmed against
      // node_modules/@babylonjs/core/Meshes/mesh.js: the merged mesh is a fresh Mesh at the
      // origin, never at any source's position), so an unparented star's own `.position` here
      // is already the world-space offset that needs to end up baked in.
      parts.push(star);
    }

    const merged = Mesh.MergeMeshes(parts, true, true);

    if (!merged) {
      // MergeMeshes returns null only on a genuine failure (an empty array, or more vertices
      // than 32-bit indices allow) — neither is reachable here (STAR_COUNT is a fixed, small
      // constant), so this is a defensive fallback, not an expected path: an empty, invisible
      // mesh means the room still works, just with no sky stars, rather than throwing out of
      // the constructor and taking the whole scene down with it.
      const fallback = CreateSphere('assistant-star-cluster', { diameter: STAR_DIAMETER_METRES }, this.scene);
      fallback.isVisible = false;
      fallback.isPickable = false;
      fallback.material = material;
      this.assistantStarsMesh = fallback;
      return;
    }

    merged.name = 'assistant-star-cluster';
    merged.material = material;
    // Never pickable and never a teleport target — the same two reasons the skybox above
    // gives, restated here because a future reader of the stars is unlikely to also read the
    // skybox's own comment: both Babylon's teleportation ray and ConnectionDrawer's own aim
    // ray pick against the scene, and floorMeshes is shared by reference with the
    // teleportation feature (CLAUDE.md, "Floor meshes are shared, not copied").
    merged.isPickable = false;
    // Deliberately NOT parented to roomOrigin (room-local, rebuilt per buildRoom()) and
    // deliberately NOT pushed into floorMeshes or this.disposables (see this field's own doc
    // comment, and STAR_COUNT's, for why).
    merged.isVisible = false;

    this.assistantStarsMesh = merged;
  }

  start(): void {
    this.engine.runRenderLoop(() => this.scene.render());
  }

  // spec: SPEC-189
  /**
   * Builds the room shell. Geometry comes from the template as parameters rather than a
   * mesh, so any client can construct it without downloading assets.
   *
   * Dispatches between two shells, based on `room.diagramId` alone — SPEC-161's own "the kind is
   * derived from `room.diagramId`, never stored": a room showing no diagram *is* the lobby
   * (SPEC-189), and a `kind` field added to the room or the template would be a second source of
   * truth that could disagree with the first (plan/lobby-v1.md, "The seam"). `buildLobbyShell`
   * (SPEC-198) and `buildWorkingRoomShell` share nothing but the accent bookkeeping just below and
   * the assistant's own light (`buildAssistantLight`, common to both — the assistant is not
   * diagram-specific and has never been part of either shell's own geometry).
   */
  buildRoom(room: RoomDto, template: RoomTemplateDto): void {
    this.clearRoom();

    const targetAccent = Color3.FromHexString(room.accentColor);

    // The colour to actually paint accent-tinted geometry with *this* build — the currently
    // displayed colour (mid-transition or settled) if one exists, otherwise the target itself,
    // which is what makes the very first room a client ever builds start at its true colour
    // with no transition to run at all. See `displayedAccentColor`'s own doc comment. Computed
    // and recorded before the shell dispatch below because both branches need it recorded even
    // though only one of them paints it: the lobby shell never carries the room's accent at all
    // (`buildLobbyShell`'s own doc comment), but `displayedAccentColor` still has to stay correct
    // for whenever the working room is shown again.
    const startAccent = this.displayedAccentColor ?? targetAccent;

    if (startAccent.equals(targetAccent)) {
      this.accentAnim = null;
      this.displayedAccentColor = targetAccent;
    } else {
      this.accentAnim = { from: startAccent, to: targetAccent, startedAtMs: performance.now() };
    }

    if (room.diagramId == null) {
      this.buildLobbyShell(template);
    } else {
      this.buildWorkingRoomShell(room, template, startAccent);
    }

    // Common to both shells — see this method's own doc comment for why.
    this.buildAssistantLight(template.floorplan.wallHeight);
  }

  // spec: SPEC-190
  /**
   * The working room: a rectangular glass-and-accent shell (plan/glass-room-tuning.md), shown
   * whenever `room.diagramId` is set (`buildRoom`'s own dispatch). This used to be the whole of
   * `buildRoom` — extracted, not rewritten, when the lobby shell (`buildLobbyShell`) needed
   * somewhere to not be built instead. `startAccent` is `buildRoom`'s own bookkeeping, passed in
   * rather than recomputed, so the two shells can never disagree about which colour a live accent
   * transition is easing from.
   *
   * No whiteboard is built here — SPEC-190 ("a whiteboard is not a fixture every room arrives
   * with," plan/lobby-v1.md) removed it from both shells outright, not just the lobby. This
   * method used to end with a framed board on the north wall (plan/whiteboard.md, "One wall,
   * framed"); every consumer of `this.whiteboard` already treats a `null` value as "no board in
   * this room" (`DrawingManager`, `ScanTool`, `ToolMode`), so simply never assigning it degrades
   * without a crash — see `ToolMode`'s own doc comment for how Draw and Scan are additionally
   * removed from the reachable mode cycle rather than left silently inert.
   */
  private buildWorkingRoomShell(room: RoomDto, template: RoomTemplateDto, startAccent: Color3): void {
    const { width, depth, wallHeight } = template.floorplan;

    // The floor grid's line colour is baked into a canvas texture (gridTexture, below) rather
    // than a material property — see FLOOR_GRID_LINE_COLOR's own doc comment for why it no
    // longer takes the room's accent as a parameter at all.
    const floorMaterial = this.material('floor', new Color3(0.22, 0.23, 0.26));
    // Tinted glass (see WALL_GLASS_ALPHA/FLOOR_GLASS_ALPHA's own doc comment): semi-transparent
    // so the skybox reads faintly through the floor, same treatment as the walls below.
    floorMaterial.alpha = FLOOR_GLASS_ALPHA;
    floorMaterial.diffuseTexture = this.gridTexture(width, depth);

    // The walls' own diffuse colour now carries the room's accent (WALL_ACCENT_TINT_STRENGTH's
    // own doc comment — the picture rail and doorway markers that used to carry it are gone).
    // Built at startAccent, not targetAccent — an actual colour change eases the rest of the way
    // via accentAnim/tickAccentColor below, painting over this starting value frame by frame; a
    // rebuild with no accent change (a resync, a different RoomDto field) has startAccent
    // already equal to targetAccent, so this is simply the final colour with nothing further to
    // animate.
    const wallColor = tintedWallColor(startAccent);

    // Subtle procedural texture: faint horizontal panel seams (matching the structural frame's
    // own horizontal beams' trim language, below) plus a fine grain, so the walls are not a
    // perfectly flat field of colour. See WallTextures.ts
    // for the named-vocabulary structure this is deliberately built as, and why it does not yet
    // select a texture per room.
    //
    // Two materials, not one, because `CreateBox`'s default UV mapping does not treat every
    // face the same way — confirmed empirically against a real `CreateBox` call (see
    // `WallTextureGenerator`'s own doc comment in WallTextures.ts for the mechanism and the
    // exact readout), not assumed from the box-builder source alone. For the north/south walls'
    // visible face, texture-u runs along the wall's own width and texture-v runs up its height —
    // the assumption the original single-material version made, and correctly so, for these two
    // walls. For the east/west walls' visible face that pairing is rotated: texture-u runs up
    // the wall's height and texture-v runs along its depth. A single shared material with one
    // uScale/vScale pair cannot reconcile both — whichever pair makes the seams horizontal and
    // evenly spaced on north/south makes them *vertical* on east/west, which is exactly the
    // headset report ("the mix between vertical and horizontal panels is a bit strange"): every
    // wall was painted with the same tile, but two of the four were reading it rotated 90°.
    // Changing which scale constant (the width-based one or the height-based one) is assigned to
    // uScale vs vScale cannot fix this on its own — the seam's orientation is baked into which
    // canvas axis WallTextures.ts drew the line on, not into the scale numbers multiplying it —
    // so `buildWallTexture`'s own `transposed` flag builds a second, genuinely transposed tile
    // (canvas width/height swapped, seam drawn along the other edge) for the east/west material,
    // and this material's own uScale/vScale are assigned to match: the wall-height-based scale
    // goes wherever *that* wall's own visible face actually maps world height onto, per wall.
    //
    // uScale on each material still repeats an undirected grain pattern with no seams of its own
    // to misalign — the room's own run length for that wall pair (width for north/south, depth
    // for east/west) rather than the previous shared `Math.max(width, depth)` approximation,
    // now that per-pair materials exist anyway to carry it; still a deliberate simplification,
    // not a correctness requirement, for the same reason the original comment gave.
    const wallMaterialNS = this.material('wall-north-south', wallColor);
    const wallTextureNS = buildWallTexture(DEFAULT_WALL_TEXTURE_KEY, this.scene, false);
    wallTextureNS.wrapU = Texture.WRAP_ADDRESSMODE;
    wallTextureNS.wrapV = Texture.WRAP_ADDRESSMODE;
    wallTextureNS.uScale = width / PANELS_WITH_GRAIN_TILE_WIDTH_METRES;
    wallTextureNS.vScale = wallHeight / PANELS_WITH_GRAIN_TILE_HEIGHT_METRES;
    wallMaterialNS.diffuseTexture = wallTextureNS;
    // Tinted glass — see WALL_GLASS_ALPHA's own doc comment.
    wallMaterialNS.alpha = WALL_GLASS_ALPHA;
    // Tracked so tickAccentColor can retint this live, whenever a transition is running — see
    // WALL_ACCENT_TINT_STRENGTH's and this field's own doc comments.
    this.wallMaterialNS = wallMaterialNS;

    const wallMaterialEW = this.material('wall-east-west', wallColor);
    const wallTextureEW = buildWallTexture(DEFAULT_WALL_TEXTURE_KEY, this.scene, true);
    wallTextureEW.wrapU = Texture.WRAP_ADDRESSMODE;
    wallTextureEW.wrapV = Texture.WRAP_ADDRESSMODE;
    wallTextureEW.uScale = wallHeight / PANELS_WITH_GRAIN_TILE_HEIGHT_METRES;
    wallTextureEW.vScale = depth / PANELS_WITH_GRAIN_TILE_WIDTH_METRES;
    wallMaterialEW.diffuseTexture = wallTextureEW;
    wallMaterialEW.alpha = WALL_GLASS_ALPHA;
    this.wallMaterialEW = wallMaterialEW;

    // `StandardMaterial.dispose()` does not free its own textures unless explicitly told to
    // (CLAUDE.md's `TransformNode.dispose()`-cascade-leaves-materials-alive trap has the same
    // shape one level up) — tracked here exactly the way `gridTexture` already tracks the
    // floor's own DynamicTexture, so a rebuild cannot leak either one. `this.material()` already
    // pushes each material itself onto `this.disposables`.
    this.disposables.push(wallTextureNS, wallTextureEW);

    // The wall glass is the working room's one remaining accent-carrying surface now (the
    // whiteboard's own frame carried a second, black one, until SPEC-190 removed the whiteboard
    // outright), so `accentAnim` no longer needs to point at a material of its own to paint:
    // `tickAccentColor` retints `wallMaterialNS`/`wallMaterialEW` directly from
    // `accentAnim.from`/`.to` every frame a transition is running. `accentAnim`/
    // `displayedAccentColor` are `buildRoom`'s own bookkeeping now, set once there before this
    // method is ever called, not repeated here.

    // The structural frame's own material — see FRAME_COLOR's own doc comment for why this is
    // deliberately separate from the wall glass above (and, until SPEC-190 removed it, from the
    // whiteboard's own second frame). A fixed colour, not room-state, so unlike the wall glass
    // this never participates in accentAnim/tickAccentColor.
    const frameMaterial = this.material('frame', FRAME_COLOR);
    // A modest self-illumination — the same reasoning boardMaterial's own below already uses:
    // this room's ambient sits deliberately low
    // (AMBIENT_INTENSITY, 0.32) so a lit and a shaded face read differently, and a flat white
    // surface with zero emissive would otherwise go duller on its shaded side than "an opaque
    // frame" is meant to read as.
    frameMaterial.emissiveColor = FRAME_COLOR.scale(0.25);

    const floor = CreateGround('floor', { width, height: depth }, this.scene);
    floor.material = floorMaterial;
    floor.parent = this.roomOrigin;
    this.floorMeshes.push(floor);
    this.disposables.push(floor);

    // The assistant's own light is built separately, once per `buildRoom()` call regardless of
    // which shell was just chosen — see `buildAssistantLight`'s own doc comment (and `buildRoom`'s,
    // for why this shell has nothing of its own to add there).

    const halfWidth = width / 2;
    const halfDepth = depth / 2;
    const thickness = 0.1;

    // Clear of the wall by a comfortable margin, and never outside a room smaller than that.
    this.spawnPoint.set(0, 0, -Math.max(halfDepth - 1.5, 0));

    const walls: { name: string; size: Vector3; at: Vector3; eastWest: boolean }[] = [
      {
        name: 'wall-north',
        size: new Vector3(width, wallHeight, thickness),
        at: new Vector3(0, wallHeight / 2, halfDepth),
        eastWest: false,
      },
      {
        name: 'wall-south',
        size: new Vector3(width, wallHeight, thickness),
        at: new Vector3(0, wallHeight / 2, -halfDepth),
        eastWest: false,
      },
      {
        name: 'wall-east',
        size: new Vector3(thickness, wallHeight, depth),
        at: new Vector3(halfWidth, wallHeight / 2, 0),
        eastWest: true,
      },
      {
        name: 'wall-west',
        size: new Vector3(thickness, wallHeight, depth),
        at: new Vector3(-halfWidth, wallHeight / 2, 0),
        eastWest: true,
      },
    ];

    for (const wall of walls) {
      const mesh = CreateBox(
        wall.name,
        { width: wall.size.x, height: wall.size.y, depth: wall.size.z },
        this.scene,
      );

      mesh.position.copyFrom(wall.at);
      // See the wall-material construction above: east/west walls need the transposed tile and
      // its own, differently-assigned uScale/vScale, because CreateBox's default UV mapping
      // rotates which axis is which between the two wall orientations.
      mesh.material = wall.eastWest ? wallMaterialEW : wallMaterialNS;
      mesh.parent = this.roomOrigin;
      this.disposables.push(mesh);

      // There used to be a picture rail here — a thin accent-tinted box, purely for spatial
      // memory. Removed outright (plan/glass-room-tuning.md item 1): the owner, after seeing the
      // white structural frame below obscure it almost entirely, asked to drop the trim strip
      // and instead let the walls' own glass carry the accent (see WALL_ACCENT_TINT_STRENGTH's
      // own doc comment) — a much larger surface that reads clearly even alongside the frame.
      // The skirting that used to sit at the floor/wall seam went the same way earlier: the
      // frame's own floor beam below owns that seam now instead of stacking a second member on
      // top of it.

      // The structural frame's own floor and top beams, one pair per wall — see
      // FRAME_COLOR/FRAME_MEMBER_THICKNESS_METRES/FRAME_TOP_BEAM_ENABLED's own doc comments for
      // the reasoning and the exact sizing. Full wall length, protruding past whichever axis
      // matches the wall's own thin dimension, so every beam reads as the same architectural
      // language as the corner pillars below rather than a mismatched assortment of trim widths.
      const beamSpecs: { suffix: string; y: number }[] = [
        { suffix: 'frame-floor', y: FRAME_MEMBER_THICKNESS_METRES / 2 },
        ...(FRAME_TOP_BEAM_ENABLED
          ? [{ suffix: 'frame-top', y: wallHeight - FRAME_MEMBER_THICKNESS_METRES / 2 }]
          : []),
      ];

      for (const spec of beamSpecs) {
        const beamWidth = wall.size.x === thickness ? FRAME_MEMBER_THICKNESS_METRES : wall.size.x;
        const beamDepth = wall.size.z === thickness ? FRAME_MEMBER_THICKNESS_METRES : wall.size.z;

        // The north wall's own top beam carries the room's name, painted directly onto its own
        // room-facing face (plan/glass-room-tuning.md, "write the room name directly on the
        // white beam") — replacing the free-standing plaque that used to mount proud of it.
        // Every other beam (this same wall's own floor beam, and every beam on the other three
        // walls) keeps the plain, shared frameMaterial. See RoomPlaque.ts's own module comment
        // for the faceUV/material reasoning this one beam needs that the others do not.
        const isNorthNameBeam = wall.name === 'wall-north' && spec.suffix === 'frame-top';

        const beam = CreateBox(
          `${wall.name}-${spec.suffix}`,
          {
            width: beamWidth,
            height: FRAME_MEMBER_THICKNESS_METRES,
            depth: beamDepth,
            // Only the name beam needs a non-default faceUV — see BEAM_NAME_FACE_UV's own doc
            // comment in RoomPlaque.ts for why every other face has to be steered away from the
            // texture's own text (CreateBox maps a texture across all six faces identically
            // unless told otherwise, CLAUDE.md-adjacent — confirmed against boxBuilder.js).
            faceUV: isNorthNameBeam ? BEAM_NAME_FACE_UV : undefined,
          },
          this.scene,
        );

        beam.position.set(wall.at.x, spec.y, wall.at.z);

        if (isNorthNameBeam) {
          // A dedicated, lit StandardMaterial — not the unlit "diffuse and emissive both read
          // the same texture" construction LabelledPlate.ts's own plates use, which would read
          // as a flat patch stuck onto a lit beam under this room's own lighting rig (CLAUDE.md:
          // "a flat unlit patch on a lit surface will not match" — the exact bug the owner is
          // asking to stop shipping). Built the same way as frameMaterial itself just below
          // (specularColor=black via `this.material()`, a matching emissive fraction), so this
          // one beam reads identically to every other plain-frameMaterial beam and pillar under
          // the room's own key/fill lights — see `buildBeamNameTexture`'s own doc comment for how
          // the texture's own background is kept from double-tinting on top of this material's
          // diffuseColor.
          const beamNameMaterial = this.material('whiteboard-frame-name', Color3.White());
          beamNameMaterial.emissiveColor = FRAME_COLOR.scale(0.25);

          const beamNameTexture = buildBeamNameTexture(
            this.scene,
            room.name,
            beamWidth,
            FRAME_MEMBER_THICKNESS_METRES,
            FRAME_COLOR.toHexString(),
          );
          beamNameMaterial.diffuseTexture = beamNameTexture;
          // `this.material()` already pushed the material itself onto `this.disposables` above —
          // the texture needs its own separate push, the same trap `wallTextureNS`/`wallTextureEW`
          // above are already careful about (`StandardMaterial.dispose()` does not free its own
          // textures unless told to).
          this.disposables.push(beamNameTexture);

          beam.material = beamNameMaterial;
        } else {
          beam.material = frameMaterial;
        }

        beam.parent = this.roomOrigin;
        beam.isPickable = false;
        this.disposables.push(beam);
      }
    }

    // The frame's own corner pillars, floor to ceiling — one per corner, not per wall: each
    // corner is shared by two walls, so this is its own loop over the four corner points rather
    // than folded into the per-wall loop above (which would build every pillar twice). See
    // FRAME_MEMBER_THICKNESS_METRES's own doc comment for why 0.18m specifically: comfortably
    // larger than the walls' own 0.05m corner overlap, so this pillar makes the corner-overlap
    // artefact structurally impossible rather than merely covered with no margin. (That doc
    // comment's own reason 2 explains the value's second, now-historical justification against
    // the picture rail, since removed.)
    const corners: { name: string; x: number; z: number }[] = [
      { name: 'north-east', x: halfWidth, z: halfDepth },
      { name: 'north-west', x: -halfWidth, z: halfDepth },
      { name: 'south-east', x: halfWidth, z: -halfDepth },
      { name: 'south-west', x: -halfWidth, z: -halfDepth },
    ];

    for (const corner of corners) {
      const pillar = CreateBox(
        `frame-pillar-${corner.name}`,
        {
          width: FRAME_MEMBER_THICKNESS_METRES,
          height: wallHeight,
          depth: FRAME_MEMBER_THICKNESS_METRES,
        },
        this.scene,
      );

      pillar.position.set(corner.x, wallHeight / 2, corner.z);
      pillar.material = frameMaterial;
      pillar.parent = this.roomOrigin;
      pillar.isPickable = false;
      this.disposables.push(pillar);
    }

    // There used to be a whiteboard here — the north wall was a framed drawing surface
    // (plan/whiteboard.md, "One wall, framed"), sized from `computeWhiteboardLayout`. SPEC-190
    // ("a whiteboard is not a fixture every room arrives with," plan/lobby-v1.md) removed it
    // outright, board, frame and all, from this shell as much as from the lobby's — see this
    // method's own doc comment for why nothing further needs to check for its absence. The pen
    // ledge that used to sit below the board was already gone before this
    // (plan/history/control-modes.md, "Remove the pen ledge entirely"); there is now nothing left
    // on this wall at all beyond the plain glass every other wall already has.

    // There used to be a doorway marker here for every template.floorplan.connectionPoints
    // entry — a faint accent-tinted box, roughly door-sized, advertising where a room *could*
    // connect. Removed outright (plan/glass-room-tuning.md item 2): it advertised a mechanism
    // DESIGN.md already replaced — Passage-as-a-walkable-doorway gave way to RoomLink, a
    // teleport target, and nobody will ever walk through the opening this marker pointed at.
    // Invisible against the old opaque walls, but read as "a weird frame, almost like a
    // doorway" once the walls went glass (the owner's own words). `connectionPoints` itself
    // stays in the floorplan — it is schema, it costs nothing, and RoomLink will plausibly want
    // it as a teleport anchor — only the drawn marker is gone.
  }

  // spec: SPEC-198
  /**
   * The lobby: SPEC-198's "simple, well-lit circular space," shown whenever `room.diagramId` is
   * `null` (`buildRoom`'s own dispatch — SPEC-189, SPEC-161). Deliberately not a variation on
   * `buildWorkingRoomShell` — no shared geometry, no shared materials, no shared light — because
   * the two are meant to read as different places at a glance, and a shell built by branching
   * inside the working room's own construction would make that harder to keep true under a future
   * retune of either one. The lobby does not carry the room's own accent colour at all: it is a
   * neutral arrival space, not per-diagram themed, so `wallMaterialNS`/`wallMaterialEW` are left
   * `null` here — already `updateRoomAccent`'s own no-op condition, and already accounted for by
   * `buildRoom`'s own accent bookkeeping running before this method is ever called.
   *
   * **Radius, not a new contract field.** `RoomFloorplanDto` stays rectangular
   * (`width`/`depth`/`wallHeight`) — no codegen, no migration, no server coupling
   * (plan/lobby-v1.md, "No contract change") — so the circle's own radius is derived from the
   * floorplan already given: the *smaller* of `width`/`depth`, so the circle always fits inside
   * whatever rectangular footprint the room was allocated rather than spilling past it. A square
   * floorplan (the seeded default, `DbSeeder.BasicRoomFloorplan`) makes this exact; a non-square
   * one leaves the circle short of the longer axis, which is a fine v1 answer to a question
   * SPEC-198 explicitly leaves open ("the radius... are open").
   *
   * **A cylinder, not a rotated disc, for both the floor and the wall.** `CreateCylinder`'s own
   * cap construction hard-codes its normal to `(0, ±1, 0)` per cap
   * (`node_modules/@babylonjs/core/Meshes/Builders/cylinderBuilder.js`, `createCylinderCap`) —
   * confirmed by reading the builder itself, not assumed — so the floor's top cap is guaranteed to
   * face straight up with no rotation to get the sign of wrong. A `CreateDisc` rotated into the
   * floor plane would need that sign worked out (and re-verified after every Babylon upgrade);
   * this shell has nothing to get backwards by construction instead. The wall reuses the same
   * choice for the identical reason on its own axis: `sideOrientation: Mesh.BACKSIDE` (not
   * `DOUBLESIDE` — CLAUDE.md's own warning about `DOUBLESIDE` plus disabled culling shipping the
   * mirrored-label bug applies to a *second, coincident* triangle set, which this mesh never has)
   * flips the cylinder's default outward-facing side normals to face inward, matching plain
   * `backFaceCulling` (left at the material default, `true`) rather than needing it disabled the
   * way the all-enclosing skybox does (`SceneRoot`'s own constructor comment) — a real difference
   * between the two: the skybox is never seen from outside itself at all, so disabling culling
   * costs nothing; this wall's *outward* face would otherwise still exist and rasterize for
   * nobody, which `BACKSIDE`'s reversed winding is what actually removes rather than merely hides.
   *
   * **Lighting is a judgement call, made deliberately rather than copied from the working room's
   * neighbouring construction:** a dedicated `HemisphericLight`, scoped to this shell alone and
   * torn down with it — see `LOBBY_AMBIENT_INTENSITY`'s own doc comment for why hemispheric
   * (no distance falloff to get wrong, unlike a `PointLight`) and why a second light rather than
   * retuning the scene-wide ambient/key/fill the working room already depends on.
   *
   * Verified visually, not merely reasoned about (CLAUDE.md, "When nothing in the project can see
   * a rendered pixel, render one for a human to look at" — `NullEngine` never rasterizes, so no
   * automated check here can say whether this reads as a room rather than a table with a rim): a
   * top-down plan view and a headless-Edge screenshot, both described in this package's own PR.
   */
  private buildLobbyShell(template: RoomTemplateDto): void {
    const { width, depth, wallHeight } = template.floorplan;
    const radius = Math.min(width, depth) / 2;

    // Clear of the wall by a comfortable margin, and never outside a room smaller than that —
    // the same shape `buildWorkingRoomShell`'s own spawn point uses, against this shell's own
    // radius rather than its half-depth.
    this.spawnPoint.set(0, 0, -Math.max(radius - 1.5, 0));

    // A dedicated bright hemispheric light, scoped to this shell alone (pushed onto
    // `this.disposables`, unlike the scene-wide ambient/key/fill built once in the constructor) —
    // see this method's own doc comment, and `LOBBY_AMBIENT_INTENSITY`'s, for why.
    const lobbyLight = new HemisphericLight('lobby-ambient', new Vector3(0, 1, 0), this.scene);
    lobbyLight.intensity = LOBBY_AMBIENT_INTENSITY;
    lobbyLight.groundColor = LOBBY_WALL_COLOR.scale(LOBBY_GROUND_FRACTION);
    this.disposables.push(lobbyLight);

    // No emissive term on either surface: emissive is added after lighting and before the clamp,
    // so on an already brightly-lit mid-tone it buys nothing but a shorter distance to saturation
    // — which is precisely how the first version of this shell went pure white (see
    // `LOBBY_WALL_COLOR`'s own doc comment).
    const floorMaterial = this.material('lobby-floor', LOBBY_FLOOR_COLOR);

    // A thin cylinder rather than a plain disc — see this method's own doc comment for why: the
    // top cap's normal is hard-coded to point straight up by the builder itself, nothing to get
    // backwards. Positioned so that top cap sits exactly at room-local y=0, the same floor plane
    // `buildWorkingRoomShell`'s own `CreateGround` floor sits on.
    const floor = CreateCylinder(
      'lobby-floor',
      {
        diameter: radius * 2,
        height: LOBBY_FLOOR_THICKNESS_METRES,
        tessellation: LOBBY_TESSELLATION,
        cap: Mesh.CAP_ALL,
      },
      this.scene,
    );
    floor.position.set(0, -LOBBY_FLOOR_THICKNESS_METRES / 2, 0);
    floor.material = floorMaterial;
    floor.parent = this.roomOrigin;
    // Shared by reference with Babylon's teleportation feature (CLAUDE.md, "Floor meshes are
    // shared, not copied") — `clearRoom` empties this array in place on every rebuild, so pushing
    // here is exactly as safe as `buildWorkingRoomShell`'s own equivalent push.
    this.floorMeshes.push(floor);
    this.disposables.push(floor);

    const wallMaterial = this.material('lobby-wall', LOBBY_WALL_COLOR);

    // No caps (the floor above already covers the bottom, and there is no ceiling — the same
    // "open above the walls" look the working room settled on). `sideOrientation: Mesh.BACKSIDE`
    // is what makes the inward-facing surface the one that actually renders — see this method's
    // own doc comment for why this, not disabling `backFaceCulling`, is the correct tool here.
    const wall = CreateCylinder(
      'lobby-wall',
      {
        diameter: radius * 2,
        height: wallHeight,
        tessellation: LOBBY_TESSELLATION,
        cap: Mesh.NO_CAP,
        sideOrientation: Mesh.BACKSIDE,
      },
      this.scene,
    );
    wall.position.set(0, wallHeight / 2, 0);
    wall.material = wallMaterial;
    wall.parent = this.roomOrigin;
    wall.isPickable = false;
    this.disposables.push(wall);
  }

  /**
   * The assistant's own light (plan/ai-in-room.md), built once per `buildRoom()` call regardless
   * of which shell was just chosen — see `buildRoom`'s own doc comment for why this is common to
   * both rather than duplicated inside `buildWorkingRoomShell`/`buildLobbyShell`: the assistant is
   * not diagram-specific, and has never been part of either shell's own geometry. Hung high in
   * the room and centred over it, at `wallHeight` plus a fixed clearance
   * (`ASSISTANT_LIGHT_HEIGHT_ABOVE_WALLS_METRES`) — roughly where a ceiling mesh used to sit, back
   * when this room had one purely to give this light a surface to hang "under". A second headset
   * session found that mesh read badly: "way up high, with a big visible gap between the walls
   * and the ceiling... didn't buy us anything. Black is fine." Removed outright, not shrunk or
   * recoloured — both shells are open above their walls now, and that gap is the intended look,
   * not a placeholder for a mesh that has not been added back yet. Nothing replaces it: there is
   * no seam to cause z-fighting against, because there is nothing there for a wall top to fight
   * with.
   *
   * Off by default; `setAssistantGlow` turns it on and colours it, and `tickAssistantLight`
   * (registered once, in the constructor) animates its intensity every frame while a turn is
   * open. Deliberately a light, not a material tint: the point of this design is that the room's
   * illumination changes, noticed without looking up — something a tint on a surface behind the
   * viewer's own gaze can never achieve, and also, as it turned out, something the ceiling
   * surface was never actually rendering at all before it was replaced by this (see this file's
   * git history for that first fix, and the note just above for why the mesh itself is gone now
   * too).
   */
  private buildAssistantLight(wallHeight: number): void {
    const assistantLight = new PointLight(
      'assistant-glow',
      new Vector3(0, wallHeight + ASSISTANT_LIGHT_HEIGHT_ABOVE_WALLS_METRES, 0),
      this.scene,
    );
    assistantLight.parent = this.roomOrigin;
    assistantLight.range = ASSISTANT_LIGHT_RANGE_METRES;
    // Every material in this room already sets specularColor to black (CLAUDE.md: specular
    // highlights on flat walls read as smears when the head moves), so this light's own
    // specular contribution is already suppressed everywhere it could land — zeroed here too,
    // for the same reason, rather than relying on that being true of every future material.
    assistantLight.specular = Color3.Black();
    assistantLight.intensity = 0;
    this.assistantLight = assistantLight;
    this.disposables.push(assistantLight);

    // Reapplied here rather than left at whatever a freshly-constructed light's own defaults
    // are: a room rebuild (a reconnect's resync, most plausibly) can land mid-turn, and the
    // glow is room state exactly like the walls' own accent colour is — it should survive a
    // rebuild, not silently reset to "none" underneath a room that still looks lit everywhere
    // else in its own memory.
    this.applyAssistantGlow();
  }

  /**
   * Retints the room's accent colour live, without going through `buildRoom` at all — the fix
   * for a headset report: "the whole top part of the walls flicker through black when the
   * colour fade starts." `buildRoom` disposes and recreates every mesh and material in the room
   * (walls, wall texture, floor, whiteboard, frame, trim) on every call, `updateRoomAccent`'s two
   * real-world callers (`main.ts`'s `hub.on('roomUpdated', ...)` and `DebugPanel.onAccentChanged`)
   * included, even though neither ever changes anything but `RoomDto.AccentColor` — confirmed
   * against the backend: `RoomUpdated` is only ever broadcast from `RoomSyncHub.UpdateRoomAccent`,
   * which touches nothing else on the room. Freshly constructed materials are not "ready" until
   * their shaders compile, and a real WebGL engine skips drawing a not-ready mesh for however
   * many frames that takes, painting the scene's own clear colour (`Color4(0.08, 0.09, 0.11, 1)`,
   * a near-black navy) in its place — invisible to `NullEngine`, which never rasterizes or tracks
   * shader-readiness at all, which is exactly why nothing here could have caught this from a
   * test. Retinting in place keeps every mesh and material instance exactly as it was — nothing
   * to recompile, nothing to flicker.
   *
   * Mirrors `buildRoom`'s own accentAnim/displayedAccentColor bookkeeping exactly — this method
   * only ever sets up *which* colour is being eased toward and from; `tickAccentColor` is what
   * actually paints every frame, for both this call site and `buildRoom`'s own. This method never
   * touches `wallMaterialNS`/`wallMaterialEW` directly, but `tickAccentColor` reads the same
   * `accentAnim`/`displayedAccentColor` this method sets, so the wall glass — the working room's
   * one remaining accent-carrying surface (`WALL_ACCENT_TINT_STRENGTH`'s own doc comment; the
   * whiteboard's own second frame is gone outright now, SPEC-190) — retints live, no rebuild, no
   * separate code path to keep in sync. A no-op while the lobby shell is showing (below). Does not
   * touch
   * the floor grid at all — see `FLOOR_GRID_LINE_COLOR`'s own doc comment: the grid's line colour
   * is a fixed neutral grey now, not derived from the room's accent, so an accent change has
   * nothing left to do to the floor. (It used to repaint the grid here, snapping straight to the
   * new accent — removed, not merely left inert, once the grid stopped reading `accent` at all.)
   * A no-op if called before the first `buildRoom()` (`wallMaterialNS`/`wallMaterialEW` still
   * `null`, per that field's own doc comment: both always exist or neither does) —
   * `roomUpdated`/`onAccentChanged` cannot fire before a room exists to update.
   */
  updateRoomAccent(accentColor: string): void {
    if (!this.wallMaterialNS || !this.wallMaterialEW) {
      return;
    }

    const targetAccent = Color3.FromHexString(accentColor);
    const startAccent = this.displayedAccentColor ?? targetAccent;

    if (startAccent.equals(targetAccent)) {
      this.accentAnim = null;
      this.displayedAccentColor = targetAccent;
    } else {
      this.accentAnim = { from: startAccent, to: targetAccent, startedAtMs: performance.now() };
    }
  }

  /**
   * Sets the assistant's own room-wide state (plan/ai-in-room.md). Idempotent — setting the
   * same glow twice touches the light once, the first time — and safe to call before the
   * first `buildRoom()`: the value is only remembered and applied once the light actually
   * exists, via `buildRoom`'s own call to `applyAssistantGlow`.
   *
   * Same name, same signature, same three states as when this tinted the ceiling's material
   * instead of lighting the room — every caller (`main.ts`'s `assistant.onGlowChanged = (glow)
   * => sceneRoot.setAssistantGlow(glow)`) and every existing test keeps working unchanged.
   * What changed is entirely inside `applyAssistantGlow` and `tickAssistantLight` below: first
   * the ceiling tint read as pure black in practice (see this file's git history for that
   * mechanism), then — a second, later headset session — the light it was replaced by read as
   * far too intense and perfectly static; see `ASSISTANT_LIGHT_RANGE_METRES`'s own doc comment
   * for the former and `tickAssistantLight` for the latter.
   *
   * A genuine transition into 'listening' or 'speaking' also resets that state's own animation
   * origin — `listeningStartedAtMs` so the breath starts calm rather than mid-swing,
   * `speakingAmplitude` so a stale sample from a previous reply can never leak into the first
   * instant of the next one.
   */
  setAssistantGlow(glow: AssistantGlow): void {
    if (glow === this.currentGlow) {
      return;
    }

    this.currentGlow = glow;

    if (glow === 'listening' || glow === 'listening-remote') {
      this.listeningStartedAtMs = performance.now();
    }

    if (glow === 'speaking') {
      this.speakingAmplitude = 0;
    }

    this.applyAssistantGlow();
  }

  /**
   * The room-wide glow currently in effect. Exists so `MicIndicatorRenderer` — the single
   * off-hand status glyph that replaced this room light's own now-deleted hand-held twin,
   * `AssistantGlowIndicatorRenderer` — can poll it every frame and fold it into that one glyph
   * (plan/mic-indicator.md) — the same "poll, don't add a second event path" shape
   * `travelSuppressed` already uses elsewhere in this project, chosen
   * here so that `AssistantController.onGlowChanged` — a single callback, already claimed by
   * `main.ts`'s own wiring straight to `setAssistantGlow` — never has to grow a second
   * listener to feed this.
   */
  get assistantGlow(): AssistantGlow {
    return this.currentGlow;
  }

  /**
   * Feeds the 'speaking' state's own per-frame amplitude — see
   * `AssistantController.onAmplitudeChanged`, driven by a `PlaybackAmplitude` analyser over
   * the reply clip's actual playback, so the light tracks what is audible rather than merely
   * "a clip is open". Merely records the sample; `tickAssistantLight` is what actually paints
   * it onto the light, every rendered frame, so a caller sampling audio at a different rate
   * than the render loop still gets smooth output. Harmless to call outside 'speaking' — there
   * is simply nothing reading `speakingAmplitude` until the state is actually 'speaking', and
   * `setAssistantGlow` resets it to 0 on every fresh transition into that state regardless.
   */
  setAssistantAmplitude(level: number): void {
    this.speakingAmplitude = Math.min(1, Math.max(0, level));
  }

  /** Sets the light's colour and its *synchronous* starting intensity the instant the glow
   *  changes — before any frame has ticked, which is what lets a caller (and several existing
   *  tests) read `intensity > 0` right after `setAssistantGlow('listening' | 'speaking')` with
   *  no render loop involved. `tickAssistantLight` takes over the intensity from here on, every
   *  frame; this method does not need to match its very first value exactly (and does not try
   *  to), only to leave the light in a reasonable, already-lit state before that starts.
   *
   *  Also sets the sky stars' own visibility and starting colour the same way, for the same
   *  reason — see `tickAssistantStars` for the per-frame half of that. 'none' hides them
   *  outright (`isVisible = false`), not merely dims them: idle has to be *fully* invisible, or
   *  every other state has to visually out-compete a glow that should not exist at all while
   *  nothing is happening. */
  private applyAssistantGlow(): void {
    if (this.assistantLight) {
      switch (this.currentGlow) {
        case 'listening':
          this.assistantLight.diffuse = LISTENING_COLOR;
          this.assistantLight.intensity = LISTENING_BASE_INTENSITY;
          break;
        case 'listening-remote':
          this.assistantLight.diffuse = LISTENING_REMOTE_COLOR;
          this.assistantLight.intensity = LISTENING_BASE_INTENSITY;
          break;
        case 'speaking':
          this.assistantLight.diffuse = SPEAKING_COLOR;
          this.assistantLight.intensity = SPEAKING_BASE_INTENSITY;
          break;
        case 'none':
          this.assistantLight.intensity = 0;
          break;
      }
    }

    if (this.assistantStarMaterial) {
      switch (this.currentGlow) {
        case 'listening':
          this.assistantStarMaterial.emissiveColor = LISTENING_COLOR.scale(LISTENING_BASE_INTENSITY);
          this.setStarsVisible(true);
          break;
        case 'listening-remote':
          this.assistantStarMaterial.emissiveColor = LISTENING_REMOTE_COLOR.scale(LISTENING_BASE_INTENSITY);
          this.setStarsVisible(true);
          break;
        case 'speaking':
          this.assistantStarMaterial.emissiveColor = SPEAKING_COLOR.scale(SPEAKING_BASE_INTENSITY);
          this.setStarsVisible(true);
          break;
        case 'none':
          this.assistantStarMaterial.emissiveColor = Color3.Black();
          this.setStarsVisible(false);
          break;
      }
    }
  }

  /**
   * Advances the room accent's own in-flight transition, every rendered frame, for as long as
   * one is running — registered once in the constructor alongside the assistant light/stars
   * ticks. A no-op the vast majority of frames: `accentAnim` is only non-null for the roughly
   * one-second window after a genuine `set_room_accent_color`/debug-panel trim change, and is
   * cleared the instant that window ends (here) or a fresh `buildRoom` supersedes it
   * (`clearRoom`).
   *
   * Paints the wall glass (`wallMaterialNS`/`wallMaterialEW`, `tintedWallColor`'d down toward
   * `WALL_BASE_COLOR` by `WALL_ACCENT_TINT_STRENGTH`) from the evolving `step.color` — the working
   * room's one remaining accent-carrying surface since the picture rail, the doorway markers, and
   * (most recently) the whiteboard's own frame all stopped carrying the accent
   * (`plan/glass-room-tuning.md`; the whiteboard itself is gone outright now, SPEC-190).
   * `wallMaterialNS`/`wallMaterialEW` are `null` while the lobby shell is showing (that field's
   * own doc comment) — `accentAnim` itself keeps advancing regardless of which shell is built, so
   * `displayedAccentColor` stays correct for whenever the working room is shown again, but the two
   * `if` guards below are what actually skip painting a material that does not exist. Reads the
   * one `accentAnim` this method already advances, so a live retint
   * (`updateRoomAccent`) or a genuine `buildRoom` change moves the walls the same way, from one
   * place.
   */
  private tickAccentColor(): void {
    const anim = this.accentAnim;

    if (!anim) {
      return;
    }

    const step = accentColorAt(
      performance.now() - anim.startedAtMs,
      anim.from,
      anim.to,
      ACCENT_TRANSITION_PHASE_MS,
    );

    this.displayedAccentColor = step.color;

    const wallTint = tintedWallColor(step.color);
    if (this.wallMaterialNS) {
      this.wallMaterialNS.diffuseColor = wallTint;
    }
    if (this.wallMaterialEW) {
      this.wallMaterialEW.diffuseColor = wallTint;
    }

    if (step.done) {
      this.accentAnim = null;
    }
  }

  private setStarsVisible(visible: boolean): void {
    if (this.assistantStarsMesh) {
      this.assistantStarsMesh.isVisible = visible;
    }
  }

  /**
   * Animates the assistant light's intensity, every rendered frame, for as long as it exists —
   * registered once in the constructor (see the note there). The fix for the second headset
   * report this light got: "the whole room lights up" (see `ASSISTANT_LIGHT_RANGE_METRES`) and
   * "static... it should show activity." 'listening' breathes on a pure function of elapsed
   * time (`breathingIntensity`); 'speaking' tracks the reply clip's own playback amplitude
   * (`speakingIntensity`, fed by `setAssistantAmplitude`) rather than time. 'none' needs no
   * per-frame work — `applyAssistantGlow` already set intensity to 0 on the transition, and
   * nothing here would change it.
   */
  private tickAssistantLight(): void {
    if (!this.assistantLight) {
      return;
    }

    switch (this.currentGlow) {
      case 'listening':
      case 'listening-remote': {
        const startedAt = this.listeningStartedAtMs ?? performance.now();
        this.assistantLight.intensity = breathingIntensity(
          performance.now() - startedAt,
          LISTENING_BASE_INTENSITY,
          LISTENING_BREATH_SWING,
          LISTENING_BREATH_PERIOD_MS,
        );
        return;
      }
      case 'speaking':
        this.assistantLight.intensity = speakingIntensity(
          this.speakingAmplitude,
          SPEAKING_BASE_INTENSITY,
          SPEAKING_AMPLITUDE_GAIN,
        );
        return;
      case 'none':
        return;
    }
  }

  /**
   * Animates the sky stars' own emissive colour every rendered frame, mirroring
   * `tickAssistantLight` exactly — same `breathingIntensity`/`speakingIntensity` functions, same
   * base/swing/gain constants, so the two channels can never drift out of agreement about how
   * "on" the assistant currently reads. 'none' needs no per-frame work: `applyAssistantGlow`
   * already hid the stars and zeroed the material on the transition, and nothing here would
   * change that.
   *
   * Deliberately not folded into `tickAssistantLight` itself: the two write to different
   * targets (a light's `.intensity`, a material's `.emissiveColor`) and keeping them as two
   * short methods called from the same observer (see the constructor) is clearer than one method
   * doing two things under one `switch`.
   */
  private tickAssistantStars(): void {
    if (!this.assistantStarMaterial) {
      return;
    }

    switch (this.currentGlow) {
      case 'listening': {
        const startedAt = this.listeningStartedAtMs ?? performance.now();
        const intensity = breathingIntensity(
          performance.now() - startedAt,
          LISTENING_BASE_INTENSITY,
          LISTENING_BREATH_SWING,
          LISTENING_BREATH_PERIOD_MS,
        );
        this.assistantStarMaterial.emissiveColor = LISTENING_COLOR.scale(intensity);
        return;
      }
      case 'listening-remote': {
        const startedAt = this.listeningStartedAtMs ?? performance.now();
        const intensity = breathingIntensity(
          performance.now() - startedAt,
          LISTENING_BASE_INTENSITY,
          LISTENING_BREATH_SWING,
          LISTENING_BREATH_PERIOD_MS,
        );
        this.assistantStarMaterial.emissiveColor = LISTENING_REMOTE_COLOR.scale(intensity);
        return;
      }
      case 'speaking': {
        const intensity = speakingIntensity(this.speakingAmplitude, SPEAKING_BASE_INTENSITY, SPEAKING_AMPLITUDE_GAIN);
        this.assistantStarMaterial.emissiveColor = SPEAKING_COLOR.scale(intensity);
        return;
      }
      case 'none':
        return;
    }
  }

  dispose(): void {
    window.removeEventListener('resize', this.handleResize);
    this.clearRoom();
    this.scene.dispose();
    this.engine.dispose();
  }

  private clearRoom(): void {
    for (const disposable of this.disposables.splice(0)) {
      disposable.dispose();
    }

    this.floorMeshes.length = 0;
    this.whiteboard = null;
    this.assistantLight = null;
    this.wallMaterialNS = null;
    this.wallMaterialEW = null;
    this.floorGridTexture = null;
    this.floorGridWidthMetres = 0;
    this.floorGridDepthMetres = 0;

    // Not displayedAccentColor — that is this room's current on-screen colour, independent of
    // any mesh's lifetime, and buildRoom (which always calls clearRoom first) reads it right
    // back as the next build's own startAccent. Clearing it here would make every rebuild, not
    // just a genuine accent change, restart the transition from scratch.
    this.accentAnim = null;
  }

  private material(name: string, diffuse: Color3): StandardMaterial {
    const material = new StandardMaterial(name, this.scene);

    material.diffuseColor = diffuse;

    // Specular highlights on flat walls read as smears when the head moves.
    material.specularColor = Color3.Black();

    this.disposables.push(material);

    return material;
  }

  /**
   * A metre grid on the floor. Not decoration: in VR a featureless floor destroys any
   * sense of scale and distance, and the grid gives one metre of reference everywhere.
   *
   * Creates a fresh `DynamicTexture` and stores it (with the room's own dimensions) on
   * `floorGridTexture`/`floorGridWidthMetres`/`floorGridDepthMetres` so `setCellGridVisible` can
   * redraw the same texture in place later, rather than this method being the only way to paint
   * one. The actual drawing lives in `paintGridTexture`, shared by both call sites.
   */
  private gridTexture(width: number, depth: number): DynamicTexture {
    const pixelsPerMetre = 64;
    const size = { width: width * pixelsPerMetre, height: depth * pixelsPerMetre };

    const texture = new DynamicTexture('floor-grid', size, this.scene, false);
    this.paintGridTexture(texture, width, depth);

    this.disposables.push(texture);
    this.floorGridTexture = texture;
    this.floorGridWidthMetres = width;
    this.floorGridDepthMetres = depth;

    return texture;
  }

  /**
   * The floor grid's own drawing, factored out of `gridTexture` so `setCellGridVisible` can
   * repaint the *existing* texture — same canvas, same size — without disposing and recreating
   * it. Pure Canvas2D drawing against whatever texture/size it is handed; the caller owns the
   * texture's lifetime.
   *
   * Wrapped in `context.save()`/`context.restore()` around the whole paint — the fix for a
   * previously open bug (`plan/open-items.md`, "`paintGridTexture` never restores canvas
   * state"): `DynamicTexture.getContext()` returns the same persistent 2D context on every call,
   * not a fresh one, so `globalAlpha` (left at 0.35 by the metre-grid stroke below, or 0.18 if
   * `paintCellGrid` ran too) used to still be active the *next* time this function ran. The very
   * first thing every repaint does is a `fillRect` meant to clear the canvas back to a solid
   * background before redrawing — at a leftover 0.35/0.18 alpha, that "clear" only partially
   * covered whatever was already there, so every repaint after the first composited its new grid
   * over the old one instead of replacing it. `save()` here captures whatever state existed
   * before this call (which, thanks to the matching `restore()` at the end of every previous
   * call, is always the context's own true default — `globalAlpha: 1`), so the `fillRect` below
   * always runs fully opaque regardless of how many times this has already repainted.
   */
  private paintGridTexture(texture: DynamicTexture, width: number, depth: number): void {
    const pixelsPerMetre = 64;
    const size = { width: width * pixelsPerMetre, height: depth * pixelsPerMetre };
    const context = texture.getContext();

    context.save();

    context.fillStyle = '#33363d';
    context.fillRect(0, 0, size.width, size.height);

    context.strokeStyle = FLOOR_GRID_LINE_COLOR;
    context.globalAlpha = 0.35;
    context.lineWidth = 2;

    for (let x = 0; x <= width; x += 1) {
      context.beginPath();
      context.moveTo(x * pixelsPerMetre, 0);
      context.lineTo(x * pixelsPerMetre, size.height);
      context.stroke();
    }

    for (let z = 0; z <= depth; z += 1) {
      context.beginPath();
      context.moveTo(0, z * pixelsPerMetre);
      context.lineTo(size.width, z * pixelsPerMetre);
      context.stroke();
    }

    if (this.cellGridVisible) {
      this.paintCellGrid(context, size, pixelsPerMetre, width, depth);
    }

    context.restore();
    texture.update();
  }

  /**
   * The finer, `CELL_SIZE`-spaced grid on top of the metre grid above — see `cellGridVisible`'s
   * own doc comment for why this exists and stays off by default. Deliberately subtler than
   * the metre grid (a lower alpha, a thinner line, its own separate fixed colour rather than
   * reusing `FLOOR_GRID_LINE_COLOR`): the metre grid is a permanent scale reference and this is
   * a toggleable debug overlay, and the two should not compete for attention when both are on.
   * Skips any line that already coincides with a whole-metre line — `CELL_SIZE` (0.3) divides
   * evenly into a metre only every 10th cell (3m), so most metre lines get no cell line drawn
   * directly on top of them, but the ones that do would otherwise double the line width there
   * for no reason.
   */
  private paintCellGrid(
    context: ReturnType<DynamicTexture['getContext']>,
    size: { width: number; height: number },
    pixelsPerMetre: number,
    width: number,
    depth: number,
  ): void {
    const pixelsPerCell = CELL_SIZE * pixelsPerMetre;

    context.strokeStyle = '#c9d1e0';
    context.globalAlpha = 0.18;
    context.lineWidth = 1;

    const isWholeMetre = (metres: number) => Math.abs(metres - Math.round(metres)) < 1e-6;

    for (let x = 0; x * CELL_SIZE <= width; x += 1) {
      const metres = x * CELL_SIZE;

      if (isWholeMetre(metres)) {
        continue;
      }

      context.beginPath();
      context.moveTo(x * pixelsPerCell, 0);
      context.lineTo(x * pixelsPerCell, size.height);
      context.stroke();
    }

    for (let z = 0; z * CELL_SIZE <= depth; z += 1) {
      const metres = z * CELL_SIZE;

      if (isWholeMetre(metres)) {
        continue;
      }

      context.beginPath();
      context.moveTo(0, z * pixelsPerCell);
      context.lineTo(size.width, z * pixelsPerCell);
      context.stroke();
    }
  }

  /**
   * Toggles the finer `CELL_SIZE` grid overlay on the floor (plan/grid.md) — wired from
   * `DebugPanel`'s own `toggle-grid` button, `lars`-only. Repaints the *existing*
   * `floorGridTexture` in place, rather than rebuilding the floor material; a no-op before the
   * first `buildRoom()` call, when there is no texture yet to repaint.
   */
  setCellGridVisible(visible: boolean): void {
    this.cellGridVisible = visible;

    if (this.floorGridTexture) {
      this.paintGridTexture(this.floorGridTexture, this.floorGridWidthMetres, this.floorGridDepthMetres);
    }
  }

  private readonly handleResize = () => this.engine.resize();
}
